import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import GetCookie from '../../utils/GetCookie';
import DocumentPreview from '../../utils/DocumentPreview';

const API_BASE_URL = "/pceapi/genbi";

const fontOptions = [
  "Aptos",
  "Aptos Black",
  "Aptos Display",
  "Aptos ExtraBold",
  "Aptos Light",
  "Aptos Mono",
  "Aptos Narrow",
  "Arial",
  "Arial Black",
  "Arial Narrow",
  "Arial Nova",
  "Arial Nova Cond",
  "Arial (Headings)",
  "Calibri",
  "Calibri Light",
  "MS PGothic",
  "Poppins",
  "Poppins Medium",
  "Poppins Semibold",
  "Times New Roman",
  "Trebuchet MS",
  "Verdana",
];

// const CITATION_STYLE_OPTIONS = [
//   { value: "apa",           label: "APA (6th Ed.)",     previewText: "(Au, YY)" },
//   { value: "ieee",          label: "IEEE (2006)",       previewText: "[1]" },
//   { value: "nice",          label: "NICE Guidelines",   previewText: "1" },
//   { value: "german",        label: "German (Footnote)", previewText: "1" },
//   { value: "uk",            label: "UK",                previewText: "1" },
//   { value: "france",        label: "France",            previewText: "1" },
//   { value: "japan",         label: "Japan",             previewText: "[1]" },
//   { value: "belgium",       label: "Belgium",           previewText: "(Au, YY)" },
//   { value: "switzerland_a", label: "Switzerland (A)",   previewText: "(Au, YY)" },
//   { value: "switzerland_b", label: "Switzerland (B)",   previewText: "(Au, YY)" },
// ];

const CITATION_STYLE_OPTIONS = [
  // Legacy styles — own code path, not config-driven
  { value: "apa",            label: "APA (6th Ed.)",       previewText: "(Au, YY)" },
  { value: "ieee",           label: "IEEE (2006)",         previewText: "[1]" },
  { value: "nice",           label: "NICE Guidelines",     previewText: "1" },
  { value: "german",         label: "German (Footnote)",   previewText: "1" },

  // Country styles — config-driven (ALL_STYLES)
  { value: "uk",             label: "UK (NICE)",           previewText: "¹" },
  { value: "france",         label: "France",              previewText: "¹" },      // italic
  { value: "japan",          label: "Japan",               previewText: "[1]" },
  { value: "germany",        label: "Germany",             previewText: "[1]" },
  { value: "canada",         label: "Canada",              previewText: "1" },
  { value: "spain",          label: "Spain",               previewText: "(Au YY)" },
  { value: "belgium_french", label: "Belgium (French)",    previewText: "Au YY" },
  { value: "belgium_dutch",  label: "Belgium (Dutch)",     previewText: "Au, YY" }, // superscript
  { value: "switzerland_a",  label: "Switzerland (A)",     previewText: "(Au, YY)" },
  { value: "switzerland_b",  label: "Switzerland (B)",     previewText: "(Au, YY)" },
];


const PAGE_SIZE_OPTIONS = [
  { value: "letter",    name: "Letter",       dims: '8.5" x 11"',      w: 8.5,  h: 11 },
  { value: "tabloid",   name: "Tabloid",      dims: '11" x 17"',       w: 11,   h: 17 },
  { value: "legal",     name: "Legal",        dims: '8.5" x 14"',      w: 8.5,  h: 14 },
  { value: "statement", name: "Statement",    dims: '5.5" x 8.5"',     w: 5.5,  h: 8.5 },
  { value: "executive", name: "Executive",    dims: '7.25" x 10.5"',   w: 7.25, h: 10.5 },
  { value: "a3",        name: "A3",           dims: '11.69" x 16.54"', w: 11.69, h: 16.54 },
  { value: "a4",        name: "A4",           dims: '8.27" x 11.69"',  w: 8.27, h: 11.69 },
  { value: "a5",        name: "A5",           dims: '5.83" x 8.27"',   w: 5.83, h: 8.27 },
  { value: "b4",        name: "B4 (JIS)",     dims: '10.12" x 14.33"', w: 10.12, h: 14.33 },
  { value: "b5",        name: "B5 (JIS)",     dims: '7.17" x 10.12"',  w: 7.17, h: 10.12 },
];

// Word-exact margin presets (values in inches)
const MARGIN_PRESETS = {
  normal:              { top: 1.0,  bottom: 1.0,  left: 1.0,   right: 1.0,   gutter: 0.0, gutterPosition: "left", mirrored: false },
  narrow:              { top: 0.5,  bottom: 0.5,  left: 0.5,   right: 0.5,   gutter: 0.0, gutterPosition: "left", mirrored: false },
  moderate:            { top: 1.0,  bottom: 1.0,  left: 0.75,  right: 0.75,  gutter: 0.0, gutterPosition: "left", mirrored: false },
  wide:                { top: 1.0,  bottom: 1.0,  left: 2.0,   right: 2.0,   gutter: 0.0, gutterPosition: "left", mirrored: false },
  mirrored:            { top: 1.0,  bottom: 1.0,  left: 1.25,  right: 1.0,   gutter: 0.0, gutterPosition: "left", mirrored: true },
  office_2003_default: { top: 1.0,  bottom: 1.0,  left: 1.25,  right: 1.25,  gutter: 0.0, gutterPosition: "left", mirrored: false },
};

const MARGIN_PRESET_OPTIONS = [
  { value: "normal",              name: "Normal",              desc: 'Top: 1"   Bottom: 1"   Left: 1"   Right: 1"' },
  { value: "narrow",              name: "Narrow",              desc: 'Top: 0.5"   Bottom: 0.5"   Left: 0.5"   Right: 0.5"' },
  { value: "moderate",            name: "Moderate",            desc: 'Top: 1"   Bottom: 1"   Left: 0.75"   Right: 0.75"' },
  { value: "wide",                name: "Wide",                desc: 'Top: 1"   Bottom: 1"   Left: 2"   Right: 2"' },
  { value: "mirrored",            name: "Mirrored",            desc: 'Top: 1"   Bottom: 1"   Inside: 1.25"   Outside: 1"' },
  { value: "office_2003_default", name: "Office 2003 Default", desc: 'Top: 1"   Bottom: 1"   Left: 1.25"   Right: 1.25"' },
  { value: "custom",              name: "Custom Margins",      desc: 'Set your own margin values' },
];

const GUTTER_POSITION_OPTIONS = ["Left", "Top"];

// Word's Bullet Library: a glyph only displays in its own font ("\uf0b7" is a dot in
// Symbol and an empty box in Arial), so each entry carries its font.
const BULLET_GLYPHS = [
  { label: "Filled dot", char: "\uf0b7", font: "Symbol" },
  { label: "Dash", char: "\uf02d", font: "Symbol" },
  { label: "Circle", char: "o", font: "Courier New" },
  { label: "Square", char: "\uf0a7", font: "Wingdings" },
  { label: "Diamond", char: "\uf075", font: "Wingdings" },
  { label: "Four diamonds", char: "\uf076", font: "Wingdings" },
  { label: "Arrowhead", char: "\uf0d8", font: "Wingdings" },
  { label: "Check mark", char: "\uf0fc", font: "Wingdings" },
  { label: "Wingdings 3 F067", char: "\uf067", font: "Wingdings 3" },
  { label: "Wingdings 3 F086", char: "\uf086", font: "Wingdings 3" },
  { label: "Keycap", char: "\u20e3", font: "MS Mincho", eastAsiaFont: "MS Mincho", hint: "eastAsia" },
];

// A numbered list's level may itself be a bullet.
const NUMBER_FORMATS = [
  { value: "decimal", label: "1, 2, 3" },
  { value: "lowerLetter", label: "a, b, c" },
  { value: "upperLetter", label: "A, B, C" },
  { value: "lowerRoman", label: "i, ii, iii" },
  { value: "upperRoman", label: "I, II, III" },
  { value: "bullet", label: "Bullet" },
];

const EMPHASIS_OPTIONS = [
  { value: "", label: "No mark" },
  { value: "none", label: "None" },
  { value: "dot", label: "Dot" },
  { value: "comma", label: "Comma" },
  { value: "circle", label: "Circle" },
  { value: "underDot", label: "Under dot" },
];

// Word rotates margins with the page when a section turns landscape (wide tables).
// Must match _build_page_margins_element() in generate_and_save_to_s3.py.
const toLandscapeMargins = (m) => ({
  ...m,
  top: m.left,
  right: m.top,
  bottom: m.right,
  left: m.bottom,
});

const MarginValues = ({ top, bottom, left, right, mirrored }) => {
  const r = (v) => `${Number(Number(v).toFixed(2))}"`;
  const cells = [
    ["Top", top], ["Bottom", bottom],
    [mirrored ? "Inside" : "Left", left], [mirrored ? "Outside" : "Right", right],
  ];
  return (
    <div className="ev-fs-10 ev-reg-p-dark w-100 px-1">
      {cells.map(([label, val]) => (
        <div key={label} className="d-flex justify-content-between text-nowrap" style={{ lineHeight: 1.35 }}>
          <span className="opacity-75">{label}</span>
          <span className="fw-semibold">{r(val)}</span>
        </div>
      ))}
    </div>
  );
};

const MarginsDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);
  const selected = MARGIN_PRESET_OPTIONS.find((o) => o.value === value) || MARGIN_PRESET_OPTIONS[0];

  useEffect(() => {
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={containerRef} className="position-relative d-inline-block w-100">
      <button
        type="button"
        className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 d-flex align-items-center gap-2 w-100 p-1 cursor-pointer"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        <div className="d-flex flex-column ms-2">
          <span className="ev-fs-11 ev-bold-reg ev-med-p-blue text-truncate">{selected.name}</span>
          <span className="ev-fs-10 ev-reg-p-dark">{selected.desc}</span>
        </div>
        <i className="bi bi-chevron-down ev-fs-11 ms-auto opacity-75" />
      </button>

      {open && (
        <div className="ev-message-card2 ev-card-3 position-absolute z-1000 w-100 mt-1 p-2">
          <div className="scrollable-element" style={{ maxHeight: "var(--px-200)", height: "auto" }}>
            {MARGIN_PRESET_OPTIONS.map((opt) => {
              const isActive = value === opt.value;
              const isHov = hovered === opt.value;
              return (
                <div
                  key={opt.value}
                  className={`d-flex align-items-center gap-2 p-2 mb-1 ev-radius-small cursor-pointer ${isActive ? "list-element-selected" : ""}`}
                  style={{
                    backgroundColor: (!isActive && isHov) ? "var(--color10)" : undefined,
                  }}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  onMouseEnter={() => setHovered(opt.value)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div className="d-flex flex-column">
                    <span className="ev-fs-11 fw-semibold">{opt.name}</span>
                    <span className="ev-fs-10 opacity-75">{opt.desc}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const MarginPreview = ({ top, bottom, left, right, gutter, gutterPosition, mirrored, landscape = false, size = 130 }) => {
  // size = long edge in px; short edge and px-per-inch scale with it
  const shortEdge = Math.round(size * 100 / 130);
  const pageW = landscape ? size : shortEdge, pageH = landscape ? shortEdge : size;
  const scale = 12 * size / 130;
  const tPx = top * scale, bPx = bottom * scale;
  const lPx = (left + (gutterPosition === "left" ? gutter : 0)) * scale;
  const rPx = right * scale;
  const tPxAdj = (top + (gutterPosition === "top" ? gutter : 0)) * scale;
  const textTop = gutterPosition === "top" ? tPxAdj : tPx;
  // Keep a visible text area even when margins exceed the page
  const textH = Math.max(pageH - textTop - bPx, 4);
  const lineCount = Math.max(Math.floor((textH - 4) / 6), 1);
  const gutterPx = gutter * scale;

  return (
    <div
      className="position-relative"
      style={{
        width: pageW,
        height: pageH,
        backgroundColor: "var(--background2)",
        border: "1px solid var(--color10)",
        borderRadius: 2,
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.12)",
        overflow: "hidden",
      }}
    >
      {gutterPx > 0 && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: gutterPosition === "top" ? "100%" : gutterPx,
            height: gutterPosition === "top" ? gutterPx : "100%",
            background: "repeating-linear-gradient(45deg, var(--color10), var(--color10) 2px, transparent 2px, transparent 4px)",
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          top: textTop,
          left: lPx,
          right: rPx,
          height: textH,
          border: "1px dashed var(--color17)",
          opacity: 0.6,
          overflow: "hidden",
        }}
      >
        {Array.from({ length: lineCount }).map((_, i) => (
          <div
            key={i}
            style={{
              margin: "4px 3px 3px",
              width: i === lineCount - 1 ? "60%" : "auto",
              height: 2,
              backgroundColor: "var(--color17)",
              opacity: 0.5,
            }}
          />
        ))}
      </div>
      {mirrored && (
        <span className="position-absolute opacity-50" style={{ bottom: 1, right: 3, fontSize: 8 }}>
          Mirrored
        </span>
      )}
    </div>
  );
};

const PageSizeDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);
  const selected = PAGE_SIZE_OPTIONS.find((o) => o.value === value) || PAGE_SIZE_OPTIONS[6];
  const THUMB_H = 28;

  useEffect(() => {
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={containerRef} className="position-relative d-inline-block w-100">
      <button
        type="button"
        className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 d-flex align-items-center gap-2 w-50 p-1 cursor-pointer"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        <div className="rfm-page-size-thumb ms-2" style={{ width: Math.round(THUMB_H * selected.w / selected.h), height: THUMB_H }} />
        <div className="d-flex flex-column">
          <span className="ev-fs-11 ev-bold-reg ev-med-p-blue text-truncate">{selected.name}</span>
          <span className="ev-fs-10 ev-reg-p-dark">{selected.dims}</span>
        </div>
        <i className="bi bi-chevron-down ev-fs-11 ms-auto opacity-75" />
      </button>

      {open && (
        <div className="ev-message-card2 ev-card-3 position-absolute z-1000 w-50 mt-1 p-2">
          <div className="scrollable-element" style={{ maxHeight: "var(--px-200)", height: "auto" }}>
            {PAGE_SIZE_OPTIONS.map((opt) => {
              const isActive = value === opt.value;
              const isHov = hovered === opt.value;
              return (
                <div
                  key={opt.value}
                  className={`d-flex align-items-center gap-2 p-2 mb-1 ev-radius-small cursor-pointer ${isActive ? "list-element-selected" : ""}`}
                  style={{
                    backgroundColor: (!isActive && isHov) ? "var(--color10)" : undefined,
                  }}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  onMouseEnter={() => setHovered(opt.value)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <div
                    className={`rfm-page-size-thumb${isActive ? " selected" : ""}`}
                    style={{ width: Math.round(THUMB_H * opt.w / opt.h), height: THUMB_H }}
                  />
                  <div className="d-flex flex-column">
                    <span className="ev-fs-11 fw-semibold">{opt.name}</span>
                    <span className="ev-fs-10 opacity-75">{opt.dims}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const fontSizeOptions = [
  2, 4, 6, 8, 10, 10.5, 12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 36, 38, 40, 42,
];
const FontSizeInput = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(String(value));
  const ref = useRef(null);

  useEffect(() => {
    setInputVal(String(value));
  }, [value]);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const commit = (raw) => {
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      const clamped = Math.min(72, Math.max(2, num));
      setInputVal(String(clamped));
      onChange(clamped);
    } else {
      setInputVal(String(value));
    }
  };

  return (
    <div ref={ref} className="position-relative d-inline-flex flex-shrink-0 me-1 w-10 rfm-fontsize-wrapper">
      <div className="d-flex align-items-center formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 w-100">
        <input
          type="text"
          className="text-center border-0 bg-transparent ev-fs-11 flex-grow-1 shadow-none rfm-input-no-outline"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
            if (e.key === "Escape") { setInputVal(String(value)); e.target.blur(); }
          }}
        />
        <button
          type="button"
          className="border-0 bg-transparent d-flex align-items-center p-0"
          tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
        >
          <i className="bi bi-chevron-down ev-fs-11 opacity-75" />
        </button>
      </div>

      {open && (
        <div className="position-absolute p-1 z-1000 ev-message-card2 ev-card-3 rounded rfm-dropdown-list-sm">
          <div className="scrollable-element vh-20">
            {fontSizeOptions.map((opt) => (
              <div
                key={opt}
                className={`px-2 py-1 rounded ev-fs-11 text-center cursor-pointer mb-1 ${
                  value === opt ? "ev-btn-blue-fill-round" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(opt);
                  setInputVal(String(opt));
                  setOpen(false);
                }}
              >
                {opt}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const lineSpacingOptions = [1.0, 1.15, 1.5, 2.0, 2.5, 3.0];
const paragraphSpacingOptions = [0, 6, 12, 18, 24, 30];

const indentOptions = [0, 0.125, 0.25, 0.375, 0.5, 0.75, 1.0, 1.25, 1.5];
const IndentInput = ({ value, onChange, className = "w-20" }) => {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(String(value));
  const ref = useRef(null);

  useEffect(() => { setInputVal(String(value)); }, [value]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const commit = (raw) => {
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      const rounded = Math.round(num * 100) / 100;
      setInputVal(String(rounded));
      onChange(rounded);
    } else {
      setInputVal(String(value));
    }
  };

  return (
    <div ref={ref} className={`position-relative d-inline-flex flex-shrink-0 me-1 ${className}`}>
      <div className="d-flex align-items-center formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 w-100">
        <input
          type="text"
          className="text-center border-0 bg-transparent ev-fs-11 flex-grow-1 shadow-none rfm-input-no-outline"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
            if (e.key === "Escape") { setInputVal(String(value)); e.target.blur(); }
          }}
        />
        <button
          type="button"
          className="border-0 bg-transparent d-flex align-items-center p-0"
          tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
        >
          <i className="bi bi-chevron-down ev-fs-11 opacity-75" />
        </button>
      </div>
      {open && (
        <div className="position-absolute top-100 end-0 mt-1 p-1 z-3 ev-message-card2 ev-card-3 rounded rfm-dropdown-list-indent">
          <div className="scrollable-element vh-20">
            {indentOptions.map((opt) => (
              <div
                key={opt}
                className={`px-2 py-1 rounded ev-fs-11 text-center cursor-pointer mb-1 ${value === opt ? "ev-btn-blue-fill-round" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); onChange(opt); setInputVal(String(opt)); setOpen(false); }}
              >
                {opt}"
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// --- NEW OFFICE-STYLE COLOR DATA ---
const THEME_COLORS = [
  // Each array is a column: [Main, Shade1, Shade2, Shade3, Shade4, Shade5]
  ["#FFFFFF", "#F2F2F2", "#D8D8D8", "#BFBFBF", "#A5A5A5", "#7F7F7F"], // White/Gray
  ["#000000", "#7F7F7F", "#595959", "#3F3F3F", "#262626", "#0C0C0C"], // Black/Dark
  ["#E7E6E6", "#D0CECE", "#AEAAAA", "#757171", "#3B3838", "#161616"], // Light Gray
  ["#44546A", "#D6DCE4", "#ADB9CA", "#8497B0", "#333F4F", "#222B35"], // Dark Blue
  ["#5B9BD5", "#DEEBF6", "#BDD7EE", "#9CC2E5", "#2E75B6", "#1E4E79"], // Blue
  ["#ED7D31", "#FBE5D5", "#F8CBAD", "#F4B083", "#C65911", "#833C0B"], // Orange
  ["#A5A5A5", "#EDEDED", "#DBDBDB", "#C9C9C9", "#7B7B7B", "#525252"], // Grey
  ["#FFC000", "#FFF2CC", "#FFE699", "#FFD966", "#BF9000", "#7F6000"], // Gold
  ["#4472C4", "#D9E2F3", "#B4C6E7", "#8EAADB", "#2F5496", "#1F3864"], // Royal Blue
  ["#70AD47", "#E2EFDA", "#C5E0B3", "#A8D08D", "#538135", "#375623"], // Green
];

const STANDARD_COLORS = [
  "#C00000", "#FF0000", "#FFC000", "#FFFF00", "#92D050", 
  "#00B050", "#00B0F0", "#0070C0", "#002060", "#7030A0"
];

const TABLE_DESIGNS = [
  { id: "design-1", color: "var(--background1)", label: "Plain" }, 
  { id: "design-2", color: "#D1D1D1", label: "Header" }, 
  { id: "design-3", color: "#D1D1D1", label: "Column" }, 
];

const TOC_DESIGNS = [
  { 
    id: "design-1", 
    label: "Standard List", 
    type: "list" 
  },
  
];

// --- Shared font style resolver used by all preview modals ---
const resolveFormatStyle = (formatObj, fallbackSize = 'inherit') => {
  if (!formatObj) return { color: 'var(--color5)' };
  const selectedFont = formatObj.fontFamily || 'inherit';
  let family = selectedFont;
  let weight = formatObj.bold ? 'bold' : 'normal';
  let style = formatObj.italic ? 'italic' : 'normal';

  if (selectedFont.includes("Aptos")) {
    if (selectedFont === "Aptos Display") { family = "Aptos Display, sans-serif"; }
    else if (selectedFont === "Aptos Mono") { family = "Aptos Mono, monospace"; }
    else if (selectedFont === "Aptos Narrow") { family = "Aptos Narrow, sans-serif"; }
    else {
      family = "Aptos, sans-serif";
      if (selectedFont === "Aptos Black") { weight = 900; }
      else if (selectedFont === "Aptos ExtraBold") { weight = 800; }
      else if (selectedFont === "Aptos Light") { weight = 300; }
    }
  } else if (selectedFont.includes("Arial")) {
    if (selectedFont === "Arial Black") { family = "Arial Black, Arial, sans-serif"; weight = 900; }
    else if (selectedFont === "Arial Narrow") { family = "Arial Narrow, Arial, sans-serif"; }
    else if (selectedFont === "Arial Nova") { family = "Arial Nova, Arial, sans-serif"; }
    else if (selectedFont === "Arial Nova Cond") { family = "Arial Nova Cond, Arial Narrow, sans-serif"; }
    else { family = "Arial, sans-serif"; }
  } else if (selectedFont.includes("Calibri")) {
    if (selectedFont === "Calibri Light") { family = "Calibri Light, Calibri, sans-serif"; weight = 300; }
    else { family = "Calibri, sans-serif"; }
  } else if (selectedFont.includes("Poppins")) {
    family = "Poppins, sans-serif";
    if (selectedFont.includes("Medium")) { weight = 500; }
    else if (selectedFont.includes("Semibold")) { weight = 600; }
  } else if (selectedFont === "Times New Roman") {
    family = '"Times New Roman", Times, serif';
  } else if (selectedFont === "Verdana") {
    family = "Verdana, Geneva, sans-serif";
  } else if (selectedFont === "MS PGothic") {
    family = '"MS PGothic", sans-serif';
  }

  return {
    fontFamily: family,
    fontSize: formatObj.fontSize ? `${formatObj.fontSize}pt` : fallbackSize,
    fontWeight: weight,
    fontStyle: style,
    color: (!formatObj.color || formatObj.color === '#000000') ? 'var(--color5)' : formatObj.color,
  };
};

// --- Table Preview Modal ---
const TablePreviewModal = ({ design, headerFormat, bodyFormat, borderColor, tableBulletIndentPt = 11, onClose }) => {
  const headers = ["NYHA Class", "Utility Value", "95% Confidence Interval"];
  const rows = [
    ["NYHA I", "0.918", "(0.906, 0.930)"],
    ["NYHA II", "0.839", "(0.827, 0.851)"],
    ["NYHA III/IV", "0.710", "(0.683, 0.737)"],
  ];
  const bulletItems = ["Symptomatic chronic heart failure with reduced ejection fraction", "NYHA class 2 to 4 symptoms", "LVEF ≤35% confirmed on echocardiogram"];

  const isHeaderColored = design.id === "design-2";
  const isColumnColored = design.id === "design-3";
  const headerBg = headerFormat?.backgroundColor;
  const effectiveColor = headerBg ? headerBg : design.color;

  const getFormatStyle = (formatObj) => resolveFormatStyle(formatObj, 'inherit');

  const getTextColor = (isHighlighted, formatObj) => {
    
    if (!formatObj?.color || formatObj.color === "#000000") {
      return "var(--color5)"; 
    }
    
    return formatObj.color;
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50"
      style={{ zIndex: "var(--num-10000000000)" }}
      onClick={onClose}
    >
      <div
        className="p-4 ev-radius-med ev-card-2"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0 fw-bold ev-fs-16" style={{ color: "var(--color5)" }}>Table Design Preview</h5>
          <button type="button" className="btn-close" onClick={onClose} style={{ filter: "var(--invert-icon)" }}></button>
        </div>

        {/* Bootstrap's .border-* utilities declare border-color with !important,
            so an inline style can never win. The colour travels as a CSS custom
            property on this wrapper and GenBIStudio.css applies it to every
            bordered descendant — cells, the note row and this box itself. */}
        <div
          className={`border ${borderColor ? "ev-table-preview-bordered" : ""}`}
          style={borderColor
            ? { borderColor, "--ev-table-border-color": borderColor }
            : { borderColor: "var(--color5)" }}
        >
          <table className="w-100">
            <thead>
              <tr>
                {headers.map((h, i) => {
                  const isHighlighted = isHeaderColored || (i === 0 && isColumnColored);
                  return (
                    <th 
                      key={i} 
                      className="p-2 text-start border-end border-bottom ev-reg-p-dark" 
                      style={{
                        ...getFormatStyle(headerFormat), 
                        backgroundColor: isHighlighted ? effectiveColor : "var(--color3)",
                        color: getTextColor(isHighlighted, headerFormat)
                      }}
                    >
                      {h}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rIndex) => (
                <tr key={rIndex}>
                  {row.map((cell, cIndex) => {
                    const isCellColored = cIndex === 0 && isColumnColored;
                    return (
                      <td 
                        key={cIndex} 
                        className={`p-2 text-start ${cIndex !== row.length - 1 ? "border-end" : ""} ev-reg-p-dark`}
                        style={{
                           ...getFormatStyle(bodyFormat), 
                           backgroundColor: isCellColored ? effectiveColor : "var(--color3)",
                           color: getTextColor(isCellColored, bodyFormat)
                        }}
                      >
                        {cell}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr>
                <td
                  className="p-2 text-start border-end border-top ev-reg-p-dark"
                  style={{ ...getFormatStyle(bodyFormat), backgroundColor: isColumnColored ? effectiveColor : "var(--color3)", color: getTextColor(isColumnColored, bodyFormat) }}
                >
                  Bullet Preview
                </td>
                <td
                  colSpan={2}
                  className="p-2 text-start border-top ev-reg-p-dark"
                  style={{ ...getFormatStyle(bodyFormat), backgroundColor: "var(--color3)", color: getTextColor(false, bodyFormat) }}
                >
                  {bulletItems.map((item, idx) => (
                    <div key={idx} style={{ display: 'flex' }}>
                      <span style={{ minWidth: `${tableBulletIndentPt * 1.33}px`, flexShrink: 0 }}>•</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </td>
              </tr>
            </tbody>
          </table>
          <div 
            className="p-2 ev-fs-12 border-top ev-reg-p-dark" 
            style={{ 
                backgroundColor: "var(--color3)",
                borderColor: borderColor || "var(--color5)" 
            }}
          >
            Note: Due to limited observations in NYHA IV, the same utility value was used for both NYHA III and IV.
          </div>
        </div>
        <div className="mt-3 text-end">
          <button className="ev-btn-blue-fill-round" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const generateTocItems = (headers, maxLevel) => {
  const counters = {};

  return headers
    .filter(h => h.level > 0 && h.level <= maxLevel)
    .map((h) => {
      counters[h.level] = (counters[h.level] || 0) + 1;

      // ✅ FIX: reset deeper levels properly
      Object.keys(counters).forEach((lvl) => {
        if (Number(lvl) > h.level) counters[lvl] = 0;
      });

      const numbering = Object.keys(counters)
        .filter(lvl => Number(lvl) <= h.level && counters[lvl] > 0)
        .map(lvl => counters[lvl])
        .join(".");

      return {
        level: h.level,
        text: `${numbering} ${h.name || "Heading"}`,
        number: numbering,
        title: h.name || "Heading",
        page: Math.floor(Math.random() * 20) + 1,
      };
    });
};
const generateFakeTocItems = (maxLevel) => {
  const items = [];

  const buildLevels = (prefix = [], level = 1) => {
    if (level > maxLevel) return;

    for (let i = 1; i <= 2; i++) {
      const newPrefix = [...prefix, i];

      items.push({
        level,
        name: `Heading ${newPrefix.join(".")}`
      });

      buildLevels(newPrefix, level + 1);
    }
  };

  buildLevels();

  return generateTocItems(items, maxLevel);
};
const TocPreviewModal = ({ onClose, format, tocLevelNum, tocNumberSpacing = 0, headingNumberPeriod = false, tocShowDots = true, headerFormats, tocStyleFormats = [] }) => {
  const realHeaderCount = headerFormats.filter(h => h.level > 0).length;

  const hasRealHeaders = realHeaderCount > 2; 

  const tocItems = hasRealHeaders
    ? generateTocItems(headerFormats, tocLevelNum)
    : generateFakeTocItems(tocLevelNum);

  // Resolve per-level style: find matching level config, fall back to level-0 (default), then to format prop
  const getStyleForLevel = (level) => {
    const levelStyle = tocStyleFormats.find((t) => t.level === level);
    const defaultStyle = tocStyleFormats.find((t) => t.level === 0);
    return resolveFormatStyle(levelStyle || defaultStyle || format);
  };

  // Level indent: read from per-level tocStyleFormats, fall back to 0.5cm per level
  const getIndentStyle = (level) => {
    const levelStyle = tocStyleFormats.find((t) => t.level === level);
    if (levelStyle && levelStyle.indentLeft !== undefined) {
      return { paddingLeft: `${levelStyle.indentLeft * 37.8}px` };
    }
    if (level <= 1) return {};
    return { paddingLeft: `${(level - 1) * 0.5 * 37.8}px` };
  };

  // Fallback line color for dotted leader
  const fallbackStyle = resolveFormatStyle(format);
  
  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50"
      style={{ zIndex: "var(--num-10000000000)" }}
      onClick={onClose}
    >
      <div
        className="p-4 ev-radius-med ev-card-2 w-35"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0 fw-bold ev-fs-16 ev-reg-p-dark">Contents (Level {tocLevelNum})</h5>
          <button type="button" className="btn-close" onClick={onClose}></button>
        </div>

        <div className="scrollable-element"
        style={{ lineHeight: "2.2" }}>
          {tocItems.map((item, index) => {
            const levelStyle = getStyleForLevel(item.level);
            const itemNumber = headingNumberPeriod && item.level === 1 ? `${item.number}.` : item.number;
            const lineColor = (levelStyle.color && levelStyle.color !== 'inherit' && levelStyle.color !== 'var(--color5)')
                              ? levelStyle.color
                              : 'var(--color5)';
            return (
            <div key={index} className="d-flex align-items-end w-100">
              {/* Left Side: Text — per-level style + indent */}
              <span
                className="text-nowrap"
                style={{ ...levelStyle, ...getIndentStyle(item.level) }}
              >
                {tocNumberSpacing > 0 ? (
                  <>
                    <span className="d-inline-block" style={{ minWidth: `${tocNumberSpacing}cm` }}>
                      {itemNumber}
                    </span>
                    {item.title}
                  </>
                ) : (
                  `${itemNumber} ${item.title}`
                )}
              </span>
              
              {/* Middle: Dotted Leader */}
              <span 
                className="flex-grow-1 mx-2 mb-2"
                style={{ 
                  borderBottom: tocShowDots ? `var(--px-2) dotted ${lineColor}` : "none", 
                  opacity: 1
                }}
              ></span>
              
              {/* Right Side: Page Number */}
              <span style={levelStyle}>
                {item.page}
              </span>
            </div>
            );
          })}
        </div>

        <div className="mt-4 text-end">
          <button className="ev-btn-blue-fill-round" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const TableDesignThumbnail = ({ design, isSelected, onClick, onPreview, headerFill, borderColor }) => {
  const isHeaderColored = design.id === "design-2";
  const isColumnColored = design.id === "design-3";
const effectiveColor = headerFill ? headerFill : design.color;
  // Falls back to the neutral token when no border colour is configured, so
  // the thumbnail looks exactly as before until the user picks one.
  const gridLine = borderColor || "var(--color17)";

  return (

    <div className="d-flex flex-column align-items-center me-2">
      
      <button 
        type="button"
        className="ev-btn-blue-fill-round ev-btn-xsmall mb-2"
        onClick={(e) => {
          e.stopPropagation();
          if (onPreview) onPreview(design);
        }}
      >
        Preview
      </button>

      <div
        className="ev-radius-small p-1 w-100 "
        style={{
          cursor: "pointer",
          border: isSelected ? "var(--px-2) solid var(--color2)" : "var(--px-1) solid var(--color17)",
          
        }}
        onClick={() => onClick(design.id)}
      >
        <div style={{ width: "100%", display: "flex", flexDirection: "column", border: `var(--px-1) solid ${gridLine}` }}>
          
          {/* Header Row */}
          <div style={{ display: "flex", borderBottom: `var(--px-1) solid ${gridLine}` }}>
             {[1, 2, 3].map((col, index) => {
                const cellColor = (isHeaderColored || (index === 0 && isColumnColored)) 
                  ? effectiveColor 
                  : "var(--color3)";

                return (
                  <div 
                    key={index}
                    style={{ 
                      flex: 1, 
                      height: "var(--px-12)", 
                      backgroundColor: cellColor,
                      borderRight: index === 2 ? "none" : `var(--px-1) solid ${gridLine}` 
                    }}
                  ></div>
                );
             })}
          </div>

          {/* Body Rows */}
          {[1, 2, 3].map((row) => (
            <div key={row} style={{ display: "flex", borderBottom: row === 3 ? "none" : `var(--px-1) solid ${gridLine}` }}>
               {[1, 2, 3].map((col, index) => {
                  const cellColor = (index === 0 && isColumnColored) ? effectiveColor : "var(--color3)"; 
                  return (
                    <div 
                      key={index} 
                      style={{ 
                        flex: 1, 
                        height: "var(--px-8)", 
                        backgroundColor: cellColor,
                        borderRight: index === 2 ? "none" : `var(--px-1) solid ${gridLine}` 
                      }}
                    ></div>
                  );
               })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CustomDropdown = ({ value, options, onChange, className, isFontSelector, disabled }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className={`position-relative d-inline-block me-1 flex-shrink-0 ${className || ""}`}
      style={{ verticalAlign: "middle" }}
    >
      <button
        type="button"
        className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 d-flex justify-content-between align-items-center"
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen(!open);
        }}
        disabled={disabled}
        style={{ 
          textAlign: "left",
          cursor: "pointer",
          paddingRight: "var(--px-8)",
          width: "100%",
          fontFamily: isFontSelector ? value : 'inherit' 
        }}
      >
        <span className="text-truncate">{value}</span>
        <i className="bi bi-chevron-down ev-fs-11" style={{opacity: 0.7 }}></i>
      </button>

      {open && (
        <div 
          className={`ev-message-card2 ev-card-3 position-absolute z-1000 mt-1 p-2 ${isFontSelector ? "" : "w-100"}`}
          style={{
            left: 0,
            minWidth: "100%",
            width: isFontSelector ? "max-content" : undefined,
          }}
        >
          <div 
            className="scrollable-element" 
            style={{ 
              maxHeight: "var(--px-200)", 
              height: "auto"
            }}
          >
            {options.map((opt) => (
              <div
                key={opt}
                className="p-2 ev-radius-small ev-fs-11"
                style={{ 
                  cursor: "pointer", 
                  backgroundColor: (hovered === opt || value === opt) ? 'var(--color2)' : "transparent",
                  color: (hovered === opt || value === opt) ? 'var(--color3)' : "inherit",
                  marginBottom: "var(--px-2)",
                  fontFamily: isFontSelector ? opt : 'inherit',
                  fontSize: isFontSelector ? '14px' : 'inherit',
                  whiteSpace: isFontSelector ? "nowrap" : "normal"
                }}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                onMouseEnter={() => setHovered(opt)}
                onMouseLeave={() => setHovered(null)}
              >
                {opt}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const eastAsiaFontOptions = [
  "None",
  "MS PGothic",
  "MS Mincho",
  "Malgun Gothic",
  "SimSun",
  "SimHei",
  "Microsoft JhengHei",
  "Yu Gothic",
];

const EastAsiaFontButton = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const current = value || "None";

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className={`ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square ${current !== "None" ? "list-element-selected" : ""}`}
        onClick={() => setOpen(!open)}
        title="East Asia Font"
      >
        <i className="bi bi-translate ev-fs-11"></i>
      </button>

      {open && (
        <div className="ev-message-card2 ev-card-3 position-absolute z-1000 p-2 rfm-popover-right">
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">East Asia Font</p>
          <div className="scrollable-element vh-20">
            {eastAsiaFontOptions.map((opt) => (
              <div
                key={opt}
                className={`px-2 py-1 rounded ev-fs-11 cursor-pointer mb-1 ${current === opt ? "ev-btn-blue-fill-round" : ""}`}
                onClick={() => { onChange(opt === "None" ? null : opt); setOpen(false); }}
              >
                {opt}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const LabeledDropdown = ({ value, options, onChange, className }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const selected = options.find((o) => o.value === value);
  const containerRef = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={containerRef} className={`position-relative d-inline-block w-100 ${className || ""}`}>
      <button
        type="button"
        className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 d-flex justify-content-between align-items-center w-100"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{ textAlign: "left", cursor: "pointer", paddingRight: "var(--px-8)" }}
      >
        <span className="text-truncate">{selected ? selected.label : value}</span>
        <i className="bi bi-chevron-down ev-fs-11" style={{ opacity: 0.7 }}></i>
      </button>

      {open && (
        <div className="ev-message-card2 ev-card-3 position-absolute z-1000 w-100 mt-1 p-2" style={{ left: 0 }}>
          <div className="scrollable-element" style={{ maxHeight: "var(--px-200)", height: "auto" }}>
            {options.map((opt) => (
              <div
                key={opt.value}
                className="p-2 ev-radius-small ev-fs-11"
                style={{
                  cursor: "pointer",
                  backgroundColor: (hovered === opt.value || value === opt.value) ? "var(--color2)" : "transparent",
                  color: (hovered === opt.value || value === opt.value) ? "var(--color3)" : "inherit",
                  marginBottom: "var(--px-2)",
                }}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                onMouseEnter={() => setHovered(opt.value)}
                onMouseLeave={() => setHovered(null)}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const _recentColorsStore = { list: [] };

// topSlot: optional node rendered at the top of the popover. Only the Table
// Design picker passes it (to host the Header/Border toggle); every other call
// site omits it and the popover renders exactly as before.
export const ColorPickerButton = ({ value, onChange, icon, iconOnly, title = "Pick color", topSlot = null }) => {
  const [open, setOpen] = useState(false);
  const [recentColors, setRecentColors] = useState([..._recentColorsStore.list]);
  const [pendingColor, setPendingColor] = useState(null);
  const wrapperRef = useRef(null);
  const colorInputRef = useRef(null);
  const colorInputActive = useRef(false);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (colorInputActive.current) return;
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setPendingColor(null);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const handleSelect = (color) => {
    const upper = color.toUpperCase();
    _recentColorsStore.list = [upper, ..._recentColorsStore.list.filter(c => c !== upper)].slice(0, 10);
    setRecentColors([..._recentColorsStore.list]);
    onChange(color);
    setOpen(false);
  };

  const openPicker = () => { if (colorInputRef.current) colorInputRef.current.click(); };

  const togglePopover = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <div ref={wrapperRef} className="position-relative d-inline-block">
      {iconOnly ? (
        <span role="button" onClick={togglePopover} title={title}>
          {icon}
        </span>
      ) : (
        <button
          type="button"
          className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square position-relative"
          onClick={togglePopover}
        >
          {icon || <span>A</span>}

          {/* Color underline */}
          <div style={{ width: "var(--px-15)", height: "var(--px-3)", backgroundColor: value || '#000',position: "absolute",
              bottom: "4px",
              left: "50%",
              transform: "translateX(-50%)" }}></div>
        </button>
      )}

      {open && (
        <div 
       
          className="ev-message-card2 ev-card-3 position-absolute top-100 z-1000 min-w-300"
          style={{  
            right: "var(--px-0)"
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {topSlot ? (
            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
              <p className="ev-fs-10 fw-bold mb-0">Theme Colors</p>
              {topSlot}
            </div>
          ) : (
            <p className="ev-fs-10 fw-bold mb-1 ">Theme Colors</p>
          )}
          
          {/* Theme Grid */}
          <div className="d-flex justify-content-between mb-2">
            {THEME_COLORS.map((column, colIdx) => (
              <div key={colIdx} className="d-flex flex-column gap-1">
                {column.map((color, rowIdx) => (
                  <button
                    key={`${colIdx}-${rowIdx}`}
                    type="button"
                    className={`p-2 ev-fs-18 ev-radius-xsmall ev-card-4 cursor-pointer${value === color ? 'selected' : ''} ${rowIdx === 0 ? 'header-gap' : ''}`}
                    style={{   
                      backgroundColor: color 
                    }}
                    onClick={() => handleSelect(color)}
                    title={color}
                  />
                ))}
              </div>
            ))}
          </div>

          <div className="border-top my-2"></div>

          <p className="ev-fs-10 fw-bold mb-1">Standard Colors</p>
          <div className="d-flex flex-wrap gap-1 mb-2">
            {STANDARD_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                className={`p-2 ev-fs-18 ev-radius-xsmall ev-card-4 cursor-pointer ${value === color ? 'selected' : ''}`}
                style={{ 
                  backgroundColor: color 
                }}
                onClick={() => handleSelect(color)}
                title={color}
              />
            ))}
          </div>

          {recentColors.length > 0 && (
            <>
              <div className="border-top my-2"></div>
              <p className="ev-fs-10 fw-bold mb-1">Recently Used Colors</p>
              <div className="d-flex flex-wrap gap-1 mb-2">
                {recentColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`p-2 ev-fs-18 ev-radius-xsmall ev-card-4 cursor-pointer ${value === color ? 'selected' : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleSelect(color)}
                    title={color}
                  />
                ))}
              </div>
            </>
          )}

          <div className="border-top my-2"></div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <label
              className="d-flex align-items-center gap-2 cursor-pointer mb-0 position-relative"
            >
              <input
                ref={colorInputRef}
                type="color"
                className="rfm-color-input-hidden"
                value={pendingColor || value || '#000000'}
                onInput={(e) => setPendingColor(e.target.value)}
                onChange={(e) => setPendingColor(e.target.value)}
              />
              <i className="bi bi-palette-fill ev-fs-14" />
              <span className="ev-fs-11">More Colors...</span>
            </label>
            {pendingColor && (
              <div className="d-flex align-items-center gap-1 ms-auto">
                <div className="p-2 ev-radius-xsmall ev-card-4 cursor-pointer" style={{ backgroundColor: pendingColor }} onClick={openPicker} title={pendingColor} />
                <button
                  type="button"
                  className="ev-btn-blue-fill-round ev-btn-xsmall"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleSelect(pendingColor); setPendingColor(null); }}
                >Save</button>
                <button
                  type="button"
                  className="ev-btn-red-fill-round ev-btn-xsmall"
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setPendingColor(null); }}
                >Cancel</button>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};
const AlignmentDropdownButton = ({ alignment, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const alignments = [
    { value: 'left', icon: <i className="bi bi-text-left"></i>, label: 'Align Left' },
    { value: 'center', icon: <i className="bi bi-text-center"></i>, label: 'Align Center' },
    { value: 'right', icon: <i className="bi bi-text-right"></i>, label: 'Align Right' },
    { value: 'justify', icon: <i className="bi bi-justify"></i>, label: 'Justify' }
  ];

  const currentAlignment = alignments.find(a => a.value === alignment) || alignments[3];

  return (
    <div ref={dropdownRef} className="position-relative d-inline-block">
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setIsOpen(!isOpen)}
        
        
        title={currentAlignment.label}
      >
        <span className="ev-align-icon">{currentAlignment.icon}</span>

      </button>

      {isOpen && (
        <div className="ev-card-3 position-absolute ev-align-dropdown">
          {alignments.map((align) => (
            <button
              key={align.value}
              type="button"
              className={`d-flex align-items-center justify-content-center ev-btn-menu-action ev-align-dropdown-item ${
                alignment === align.value ? 'active' : ''
              }`}
              onClick={() => { onChange(align.value); setIsOpen(false); }}
              title={align.label}
            >
              <span className="ev-align-dropdown-item-icon">{align.icon}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const BorderDropdownButton = ({ borders, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const borderOptions = [
    { value: 'bottom', icon: <i className="bi bi-border-bottom"></i>, label: 'Bottom Border' },
    { value: 'top', icon: <i className="bi bi-border-top"></i>, label: 'Top Border' },
    { value: 'left', icon: <i className="bi bi-border-left"></i>, label: 'Left Border' },
    { value: 'right', icon: <i className="bi bi-border-right"></i>, label: 'Right Border' },
    { value: 'none', icon: <i className="bi bi-border"></i>, label: 'No Border' },
    { value: 'outside', icon: <i className="bi bi-border-outer"></i>, label: 'Outside Border' },
    // Not a border choice: the backend keeps the paragraph style and builds no box at all.
    { value: 'nobox', icon: <i className="bi bi-x-square"></i>, label: 'No Box' },
  ];

  const currentBorder = borderOptions.find(b => b.value === borders) || borderOptions[5];

  return (
    <div ref={dropdownRef} className="position-relative d-inline-block">
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setIsOpen(!isOpen)}
        title={currentBorder.label}
      >
        <span className="ev-align-icon">{currentBorder.icon}</span>
      </button>

      {isOpen && (
        <div className="ev-card-3 position-absolute ev-align-dropdown">
          {borderOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`d-flex align-items-center justify-content-center ev-btn-menu-action ev-align-dropdown-item ${
                borders === opt.value ? 'active' : ''
              }`}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              title={opt.label}
            >
              <span className="ev-align-dropdown-item-icon">{opt.icon}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const UNDERLINE_OPTIONS = [
  { value: 'single',   label: 'Single',   className: 'rfm-underline-preview-single' },
  { value: 'double',   label: 'Double',   className: 'rfm-underline-preview-double' },
  { value: 'thick',    label: 'Thick',    className: 'rfm-underline-preview-thick' },
  { value: 'dotted',   label: 'Dotted',   className: 'rfm-underline-preview-dotted' },
  { value: 'dash',   label: 'Dashed',   className: 'rfm-underline-preview-dashed' },
  { value: 'wavy',     label: 'Wave',     isWave: true },
];

const UnderlineDropdownButton = ({ underline, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = underline && underline !== 'none';

  return (
    <div ref={dropdownRef} className="position-relative d-inline-block">
      <button
        type="button"
        className={`ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square rfm-underline-btn ${isActive ? 'active list-element-selected' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="Underline"
      >
        <i className="bi bi-type-underline"></i>
      </button>

      {isOpen && (
        <div className="ev-card-4 position-absolute p-2 rfm-underline-dropdown">
          {UNDERLINE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`ev-btn-menu-action mb-1 ${underline === opt.value ? 'active' : ''}`}
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              title={opt.label}
            >
              {opt.isWave ? (
                <div className="rfm-underline-preview">
                  <svg className="rfm-underline-wave-svg" viewBox="0 0 60 6" preserveAspectRatio="none">
                    <path d="M0 3 Q 3 0, 6 3 T 12 3 T 18 3 T 24 3 T 30 3 T 36 3 T 42 3 T 48 3 T 54 3 T 60 3" stroke="currentColor" fill="none" strokeWidth="1"/>
                  </svg>
                </div>
              ) : (
                <div className={`rfm-underline-preview ${opt.className}`}></div>
              )}
            </button>
          ))}
          <div className="border-top mt-1 pt-1">
            <button
              type="button"
              className={`ev-btn-menu-action ${!underline || underline === 'none' ? 'active' : ''}`}
              onClick={() => { onChange('none'); setIsOpen(false); }}
            >
              <span className="ev-fs-11">None</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MoreOptionsDropdown = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className="position-relative d-inline-block">
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setIsOpen(!isOpen)}
        title="More options"
      >
        <span className="ev-align-icon"><i className="bi bi-three-dots-vertical"></i></span>
      </button>

      {isOpen && (
        <div className="ev-card-4 position-absolute rfm-popover-right rfm-more-options-popover d-flex align-items-center">
          {children}
        </div>
      )}
    </div>
  );
};

const FormatControls = ({ format, onChange, children, fontFamilyWidth = "w-25", hideLineSpacing, hideSpacing }) => {
  const update = (patch) => onChange({ ...format, ...patch });

  return (
    <>
      <CustomDropdown
        value={format.fontFamily}
        options={fontOptions}
        className={`${fontFamilyWidth} flex-shrink-0`}
        onChange={(val) => update({ fontFamily: val })}
        isFontSelector={true}
      />

      <FontSizeInput
        value={format.fontSize}
        onChange={(val) => update({ fontSize: val })}
      />

      <button
        type="button"
        className={`ev-btn-small ev-message-card2 ev-card-3 me-1 ev-btn-border-square ${format.bold ? 'ev-bold-large list-element-selected' : 'ev-reg'}`}
        onClick={() => update({ bold: !format.bold })}
      >
        B
      </button>

      <button
        type="button"
        className={`ev-btn-small ev-message-card2 ev-card-3 ev-btn-border-square ${format.italic ? 'ev-bold-large list-element-selected' : 'ev-reg'}`}
        onClick={() => update({ italic: !format.italic })}
        style={{ fontStyle: "italic"}}
      >
        I
      </button>
      
      <ColorPickerButton
        value={format.color}
        onChange={(color) => update({ color })}
      />

      {!hideSpacing && (
        <SpacingPopoverButton format={format} onChange={onChange} hideLineSpacing={hideLineSpacing} />
      )}
      {children}
    </>
  );
};


const SpacingInput = ({ value, onChange, options, displayOptions, label, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(String(value));
  const ref = useRef(null);

  useEffect(() => {
    setInputVal(String(value));
  }, [value]);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const commit = (raw) => {
    if (readOnly) return;
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      const clamped = Math.min(100, Math.max(0, num)); // 0-100 range
      setInputVal(String(clamped));
      onChange(clamped);
    } else {
      setInputVal(String(value));
    }
  };

  return (
    <div ref={ref} className="position-relative d-inline-flex flex-shrink-0 w-100">
      <div className="d-flex align-items-center formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 w-100">
        <input
          type="text"
          className={`text-center border-0 bg-transparent ev-fs-11 flex-grow-1 shadow-none rfm-input-no-outline${readOnly ? ' cursor-pointer' : ''}`}
          value={inputVal}
          readOnly={readOnly}
          onChange={(e) => { if (!readOnly) setInputVal(e.target.value); }}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.target.blur();
            if (e.key === "Escape") { setInputVal(String(value)); e.target.blur(); }
          }}
        />
        <button
          type="button"
          className="border-0 bg-transparent d-flex align-items-center p-0"
          tabIndex={-1}
          onClick={(e) => { e.stopPropagation(); setOpen((p) => !p); }}
        >
          <i className="bi bi-chevron-down ev-fs-11 opacity-75" />
        </button>
      </div>

      {open && (
        <div className="position-absolute top-100 start-0 w-100 mt-1 p-1 z-3 ev-message-card2 ev-card-3 rounded">
          <div className="scrollable-element vh-10">
            {options.map((opt, idx) => (
              <div
                key={opt}
                className={`px-2 py-1 rounded ev-fs-11 text-center cursor-pointer mb-1 ${
                  value === opt ? "ev-btn-blue-fill-round" : ""
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(opt);
                  setInputVal(String(opt));
                  setOpen(false);
                }}
              >
                {displayOptions ? displayOptions[idx] : opt}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const NumberStepperInput = ({ value, onChange, step = 1, unit, min, max, showRangeWarning = false }) => {
  const clampValue = (num) => {
    const minClamped = min !== undefined && num < min ? min : num;
    return max !== undefined && minClamped > max ? max : minClamped;
  };
  const getDisplayValue = (nextValue) => {
    const num = parseFloat(nextValue);
    return !isNaN(num) ? String(clampValue(num)) : String(value ?? 0);
  };
  const [inputVal, setInputVal] = useState(() => getDisplayValue(value ?? 0));
  const [warning, setWarning] = useState("");
  const warningTimerRef = useRef(null);

  useEffect(() => { setInputVal(getDisplayValue(value ?? 0)); }, [value, min, max]);

  useEffect(() => () => { if (warningTimerRef.current) clearTimeout(warningTimerRef.current); }, []);

  const triggerWarning = () => {
    if (!showRangeWarning) return;
    setWarning(`Value must be between ${min} and ${max}${unit ? ` ${unit}` : ""}`);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    warningTimerRef.current = setTimeout(() => setWarning(""), 2000);
  };

  const commit = (raw) => {
    const num = parseFloat(raw);
    if (!isNaN(num)) {
      const clamped = clampValue(num);
      if (clamped !== num) triggerWarning();
      onChange(clamped);
      setInputVal(String(clamped));
    } else {
      setInputVal(getDisplayValue(value ?? 0));
    }
  };

  return (
    <div className="d-flex align-items-center formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 w-100 position-relative">
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        className="text-center border-0 bg-transparent ev-fs-11 flex-grow-1 shadow-none rfm-input-no-outline rfm-stepper-input"
        value={inputVal}
        onChange={(e) => {
          const val = e.target.value;
          const num = parseFloat(val);

          if (!isNaN(num)) {
            const clamped = clampValue(num);
            if (clamped !== num) triggerWarning();
            setInputVal(clamped !== num ? String(clamped) : val);
            onChange(clamped);
          } else {
            setInputVal(val);
          }
        }}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.target.blur();
    if (e.key === "Escape") {
      setInputVal(getDisplayValue(value ?? 0));
      e.target.blur();
    }
        }}
      />
      {unit && <span className="ev-fs-11 ev-reg-p-dark ps-1 pe-1">{unit}</span>}
      {warning && (
        <div className="ev-message-card2 ev-card-3 border rounded px-2 py-1 ev-fs-10 ev-reg-p-dark rfm-stepper-warning">
          {warning}
        </div>
      )}
    </div>
  );
};
const TocOptionsButton = ({ tocLevelNum, onTocLevelChange, tocNumberSpacing, onTocNumberSpacingChange, tocShowDots, onTocShowDotsChange, headingNumberPeriod, onHeadingNumberPeriodChange, includeLists, onIncludeListsChange, maxHeaderLevel = 9 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const levelOptions = Array.from({ length: maxHeaderLevel }, (_, i) => i + 1);
  const levelDisplayOptions = levelOptions.map((n) => `Level ${n}`);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className="ev-message-card2 ev-card-3 ev-btn-border-square ev-btn-small ms-1"
        onClick={() => setOpen(!open)}
        title="TOC Options"
      >
        <i className="bi bi-list-nested"></i>
      </button>

      {open && (
        <div
          className="ev-message-card2 ev-card-3 position-absolute z-1000 p-2 rfm-popover-right"
        >
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">TOC Options</p>

          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="ev-fs-11 ev-reg-p-dark">TOC Level</span>
            <div className="rfm-popover-input-col">
              <SpacingInput
                value={tocLevelNum}
                options={levelOptions}
                displayOptions={levelDisplayOptions}
                onChange={(val) => onTocLevelChange(Number(val))}
                readOnly={true}
              />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              className="ev-fs-11 ev-reg-p-dark me-2"
            >
              Space after number
            </span>
            <div className="rfm-popover-input-col">
              <NumberStepperInput
                value={tocNumberSpacing ?? 0}
                step={0.1}
                unit="cm"
                min={0}
                max={5}
                showRangeWarning
                onChange={(val) => onTocNumberSpacingChange(val)}
              />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              className="ev-fs-11 ev-reg-p-dark me-2"
              title="Dotted line between the heading and the page number in the table of contents."
            >
              Show dots
            </span>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={tocShowDots ?? true}
                onChange={(e) => onTocShowDotsChange(e.target.checked)}
              />
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between mb-2">
            <span
              className="ev-fs-11 ev-reg-p-dark me-2"
              title="Adds a period after main heading numbers (1., 2., 3.) in the report and the table of contents. Sub-headings (1.1, 1.2) stay unchanged."
            >
              Number period
            </span>
            <div className="form-check form-switch mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                checked={headingNumberPeriod ?? false}
                onChange={(e) => onHeadingNumberPeriodChange(e.target.checked)}
              />
            </div>
          </div>

          <p className="ev-fs-10 fw-bold mb-2 mt-2 ev-reg-p-dark border-top pt-2">Include in report</p>
          {[
            { key: "toc", label: "Table of Contents" },
            { key: "lot", label: "List of Tables" },
            { key: "lof", label: "List of Figures" },
            { key: "abbreviations", label: "List of Abbreviations" },
          ].map(({ key, label }) => (
            <div key={key} className="d-flex align-items-center justify-content-between mb-2">
              <span className="ev-fs-11 ev-reg-p-dark me-2">{label}</span>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={includeLists?.[key] ?? true}
                  onChange={(e) => onIncludeListsChange({ ...includeLists, [key]: e.target.checked })}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const BibPreviewModal = ({ onClose, bibIndent, bibLeftMargin, refStyle }) => {
  const sampleEntries = [
    { num: 1, text: "Frías JP, Davies MJ, Rosenstock J, et al. Tirzepatide versus Semaglutide Once Weekly in Patients with Type 2 Diabetes. Massachusetts Medical Society, 2021. DOI: 10.1056/NEJMoa2107519" },
    { num: 2, text: "Evans J, Graham B, Pordeli P, et al. Time for a Time Window Extension: Insights from Late Presenters in the ESCAPE Trial. American Journal of Neuroradiology (AJNR), 2017." },
  ];

  const bibIndent1 = bibIndent ?? 0.25;
  const bibLeftMargin1 = bibLeftMargin ?? 0;
  const totalLeftPx = (bibLeftMargin1 + bibIndent1) * 96;
  const hangIndentPx = bibIndent1 * 96;

  const resolvedStyle = resolveFormatStyle(refStyle, "10pt");

  return createPortal(
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50"
      style={{ zIndex: 1000000001 }}
      onClick={onClose}
    >
      <div
        className="p-4 ev-radius-med ev-card-2 w-35 rfm-bib-modal-inner"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="m-0 fw-bold ev-fs-16" style={{ color: "var(--color5)" }}>Bibliography Preview</h5>
          <button type="button" className="btn-close" onClick={onClose} style={{ filter: "var(--invert-icon)" }}></button>
        </div>

        <div className="p-3 ev-card-4 ev-radius-small rfm-bib-content">
          {sampleEntries.map((entry, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                alignItems: "flex-start",
                paddingLeft: `${totalLeftPx - hangIndentPx}px`,
                lineHeight: refStyle?.lineSpacing ?? 1.0,
                marginBottom: idx < sampleEntries.length - 1 ? `${Math.max((refStyle?.spaceAfter ?? 0) * 2, 8)}px` : 0,
                ...resolvedStyle,
              }}
              className="ev-reg-p-dark"
            >
              <span className="fw-bold flex-shrink-0" style={{ width: `${hangIndentPx}px` }}>
                {entry.num}.
              </span>
              <span className="rfm-bib-entry-text">
                {entry.text}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-3 text-end">
          <button className="ev-btn-blue-fill-round" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const BibOptionsButton = ({ bibIndent, onBibIndentChange, bibLeftMargin, onBibLeftMarginChange, refStyle, onRefStyleChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setOpen(!open)}
        title="Indent and Spacing Options"
      >
        <i className="bi bi-distribute-vertical ev-fs-11"></i>
      </button>

      {open && (
        <div
          className="ev-message-card2 ev-card-3 position-absolute z-1000 p-3 rfm-popover-right rfm-spacing-indent-popover"
        >
          {/* INDENTATION SECTION */}
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">Indentation</p>

          <div className="row g-3 mb-2">
            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Indent</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={bibIndent}
                    step={0.1}
                    unit="in"
                    onChange={(val) => onBibIndentChange(val)}
                  />
                </div>
              </div>
            </div>

            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Left Margin</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={bibLeftMargin}
                    step={0.1}
                    unit="in"
                    onChange={(val) => onBibLeftMarginChange(val)}
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="my-2" />

          {/* SPACING SECTION */}
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">Spacing</p>

          <div className="row g-3">
            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Space Before</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={refStyle?.spaceBefore ?? 0}
                    step={1}
                    unit="pt"
                    onChange={(val) => onRefStyleChange({ ...refStyle, spaceBefore: val })}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Space After</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={refStyle?.spaceAfter ?? 0}
                    step={1}
                    unit="pt"
                    onChange={(val) => onRefStyleChange({ ...refStyle, spaceAfter: val })}
                  />
                </div>
              </div>
            </div>

            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Line Spacing</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={refStyle?.lineSpacing ?? 1.0}
                    step={0.05}
                    onChange={(val) => onRefStyleChange({ ...refStyle, lineSpacing: val })}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BulletIndentButton = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setOpen(!open)}
        title="Bullet Indent"
      >
        <i className="bi bi-text-indent-left"></i>
      </button>

      {open && (
        <div
          className="ev-message-card2 ev-card-3 position-absolute z-1000 p-2 rfm-popover-right"
        >
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">Bullet Indent</p>
          <div className="d-flex align-items-center justify-content-between">
            <span className="ev-fs-11 ev-reg-p-dark">Indent (pt)</span>
            <div className="rfm-bullet-input-col">
              <SpacingInput
                value={value}
                options={[6, 8, 10, 11, 12, 14, 16, 18, 20]}
                onChange={(val) => onChange(Number(val))}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TWIPS_PER_CM = 567;
const PREVIEW_TWIPS_PER_PX = 25;
const NUMBER_SAMPLE = { decimal: "1", lowerLetter: "a", upperLetter: "A", lowerRoman: "i", upperRoman: "I" };
const UNDERLINE_CSS = { double: "double", dotted: "dotted", dash: "dashed", wavy: "wavy" };

// A new list starts with all 9 levels filled with the backend's defaults, like Word's.
const makeListLevels = (kind) =>
  Array.from({ length: 9 }, (_, i) => {
    const base = { indentLeft: 720 * (i + 1), indentHanging: 360 };
    if (kind === "number") {
      return { ...base, numFmt: ["decimal", "lowerLetter", "lowerRoman"][i % 3], lvlText: `%${i + 1}.` };
    }
    const g = BULLET_GLYPHS[[0, 2, 4][i % 3]];
    return { ...base, numFmt: "bullet", lvlText: g.char, font: g.font, hint: "default" };
  });

// A list is linked only to the matching slot: bullet lists to "-", numbered lists to "1.".
const listKind = (list) => list.kind || (list.levels?.[0]?.numFmt === "bullet" ? "bullet" : "number");

const markerText = (spec) =>
  spec.numFmt === "bullet" ? spec.lvlText : (spec.lvlText || "").replace(/%\d/g, NUMBER_SAMPLE[spec.numFmt] || "1");

const markerStyle = (spec) => ({
  fontFamily: spec.font ? `"${spec.font}"` : undefined,
  color: spec.color,
  fontWeight: spec.bold ? 700 : 400,
  fontStyle: spec.italic ? "italic" : "normal",
  fontSize: spec.fontSize ? `${spec.fontSize}pt` : undefined,
  textDecorationLine: spec.underline && spec.underline !== "none" ? "underline" : "none",
  textDecorationStyle: UNDERLINE_CSS[spec.underline],
  textDecorationColor: spec.underlineColor,
});

// The 9 levels drawn like Word's preview pane; a click picks the level to modify.
const ListPreview = ({ levels, selected, onSelect }) => (
  <div className="ev-message-card2 ev-card-3 ev-radius-small p-2 flex-grow-1">
    {levels.map((spec, i) => (
      <div
        key={i}
        className="d-flex align-items-center cursor-pointer"
        style={{ paddingLeft: (spec.indentLeft - spec.indentHanging) / PREVIEW_TWIPS_PER_PX, minHeight: "var(--px-20)" }}
        onClick={() => onSelect(i)}
      >
        <span style={{ ...markerStyle(spec), minWidth: spec.indentHanging / PREVIEW_TWIPS_PER_PX, whiteSpace: "nowrap" }}>
          {markerText(spec)}
        </span>
        <div
          className="flex-grow-1 ms-1"
          style={{ height: i === selected ? 4 : 3, backgroundColor: i === selected ? "#000" : "#B0B0B0" }}
        />
      </div>
    ))}
  </div>
);

// Word's "Define new Multilevel list": level picker, preview, number format, position.
const ListEditor = ({ list, onChange, onDelete }) => {
  const [level, setLevel] = useState(0);
  const spec = list.levels[level];
  const update = (patch) =>
    onChange({ ...list, levels: list.levels.map((s, i) => (i === level ? { ...s, ...patch } : s)) });

  const glyphIdx = BULLET_GLYPHS.findIndex((g) => g.char === spec.lvlText && g.font === spec.font);
  const styleValue = spec.numFmt === "bullet" ? `bullet:${glyphIdx}` : spec.numFmt;
  const styleOptions = [
    ...NUMBER_FORMATS.filter((f) => f.value !== "bullet"),
    ...BULLET_GLYPHS.map((g, i) => ({
      value: `bullet:${i}`,
      label: <span>Bullet, {g.label}: <span style={{ fontFamily: `"${g.font}"` }}>{g.char}</span></span>,
    })),
  ];
  const onStyle = (val) => {
    if (val.startsWith("bullet:")) {
      const g = BULLET_GLYPHS[Number(val.split(":")[1])];
      update({ numFmt: "bullet", lvlText: g.char, font: g.font, hint: g.hint || "default",
               eastAsiaFont: g.eastAsiaFont || spec.eastAsiaFont });
    } else if (spec.numFmt === "bullet") {
      update({ numFmt: val, lvlText: `%${level + 1}.`, font: undefined, hint: undefined });
    } else {
      update({ numFmt: val });
    }
  };

  const cm = (twips) => Math.round((twips / TWIPS_PER_CM) * 100) / 100;
  const alignedCm = cm(spec.indentLeft - spec.indentHanging);
  const textCm = cm(spec.indentLeft);
  const setPosition = (aligned, text) =>
    update({
      indentLeft: Math.round(Math.max(aligned, text) * TWIPS_PER_CM),
      indentHanging: Math.round(Math.max(0, text - aligned) * TWIPS_PER_CM),
    });

  return (
    <div className="ev-message-card2 ev-card-3 ev-radius-small p-3 mb-3">
      <div className="d-flex align-items-center gap-2 mb-2 ev-fs-11 ev-reg-p-dark">
        <span className="ev-btn-blue-fill-round ev-btn-xsmall d-flex align-items-center gap-1">
          <i className={`bi ${listKind(list) === "bullet" ? "bi-list-ul" : "bi-list-ol"}`}></i>
          {listKind(list) === "bullet" ? "Bullet list" : "Numbered list"}
        </span>
        <label className="mb-0 ms-2">List name:</label>
        <input
          type="text"
          className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 ev-fs-12 px-2"
          style={{ width: "var(--px-200)" }}
          value={list.name}
          onChange={(e) => onChange({ ...list, name: e.target.value })}
        />
        <button type="button" className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 py-1 ms-auto" onClick={onDelete} title="Delete list">
          <i className="bi bi-trash" />
        </button>
      </div>

      <p className="ev-fs-11 ev-reg-p-dark mb-1">Click level to modify:</p>
      <div className="d-flex gap-2 mb-3">
        <div className="d-flex flex-column">
          {list.levels.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`ev-btn-xsmall ev-fs-11 mb-1 ${i === level ? "ev-btn-blue-fill-round" : "ev-btn-border-square ev-message-card2 ev-card-3"}`}
              onClick={() => setLevel(i)}
            >
              {i + 1}
            </button>
          ))}
        </div>
        <ListPreview levels={list.levels} selected={level} onSelect={setLevel} />
      </div>

      <p className="ev-bold-reg ev-fs-12 ev-reg-p-dark mb-1">Number format</p>
      <div className="row g-2 align-items-center mb-2 ev-fs-11 ev-reg-p-dark">
        <label className="col-4">Enter formatting for number:</label>
        <div className="col-8">
          <input
            type="text"
            className="formField3 ev-btn-border-square ev-btn-small ev-fs-12 w-100"
            style={markerStyle(spec)}
            value={spec.lvlText || ""}
            disabled={spec.numFmt === "bullet"}
            title={spec.numFmt === "bullet" ? "Pick the bullet below" : `%${level + 1} is the counter, e.g. %${level + 1}) or (%${level + 1})`}
            onChange={(e) => update({ lvlText: e.target.value })}
          />
        </div>
        <label className="col-4">Number style for this level:</label>
        <div className="col-8">
          <LabeledDropdown value={styleValue} options={styleOptions} onChange={onStyle} />
        </div>
        <label className="col-4">Font:</label>
        <div className="col-8 d-flex align-items-center flex-wrap gap-1">
          {spec.numFmt !== "bullet" && (
            <CustomDropdown
              value={spec.font || "Font"}
              options={fontOptions}
              isFontSelector={true}
              onChange={(val) => update({ font: val })}
            />
          )}
          <EastAsiaFontButton value={spec.eastAsiaFont} onChange={(val) => update({ eastAsiaFont: val })} />
          <FontSizeInput value={spec.fontSize ?? ""} onChange={(val) => update({ fontSize: val })} />
          <button
            type="button"
            className={`ev-btn-small ev-message-card2 ev-card-3 ev-btn-border-square ${spec.bold ? "ev-bold-large list-element-selected" : "ev-reg"}`}
            onClick={() => update({ bold: !spec.bold })}
          >
            B
          </button>
          <button
            type="button"
            className={`ev-btn-small ev-message-card2 ev-card-3 ev-btn-border-square ${spec.italic ? "ev-bold-large list-element-selected" : "ev-reg"}`}
            onClick={() => update({ italic: !spec.italic })}
            style={{ fontStyle: "italic" }}
          >
            I
          </button>
          <ColorPickerButton value={spec.color} onChange={(color) => update({ color })} />
          <UnderlineDropdownButton underline={spec.underline || "none"} onChange={(u) => update({ underline: u })} />
          <ColorPickerButton
            value={spec.underlineColor}
            onChange={(color) => update({ underlineColor: color })}
            icon={<i className="bi bi-type-underline"></i>}
            title="Underline color"
          />
          <div style={{ width: "var(--px-100)" }}>
            <LabeledDropdown
              value={spec.emphasis || ""}
              options={EMPHASIS_OPTIONS}
              onChange={(val) => update({ emphasis: val || undefined })}
            />
          </div>
        </div>
      </div>

      <p className="ev-bold-reg ev-fs-12 ev-reg-p-dark mb-1">Bullet position</p>
      <div className="d-flex align-items-center gap-3 ev-fs-11 ev-reg-p-dark">
        <span>Aligned at:</span>
        <div style={{ width: "var(--px-100)" }}>
          <NumberStepperInput value={alignedCm} step={0.1} min={0} unit="cm" onChange={(a) => setPosition(a, textCm)} />
        </div>
        <span>Text indent at:</span>
        <div style={{ width: "var(--px-100)" }}>
          <NumberStepperInput value={textCm} step={0.1} min={0} unit="cm" onChange={(t) => setPosition(alignedCm, t)} />
        </div>
      </div>
    </div>
  );
};

// Lists are defined once, then each paragraph style links a bullet list and a numbered list.
// The default paragraph's links also drive body, table and image-footer lists.
const ListsSection = ({ lists, setLists, paragraphFormats, updateParagraphFormat }) => {
  const [openId, setOpenId] = useState(lists[0]?.id || null);
  const openList = lists.find((l) => l.id === openId);
  const linkOptions = (kind) => [
    { value: "", label: "Default" },
    ...lists.filter((l) => listKind(l) === kind).map((l) => ({ value: l.id, label: l.name })),
  ];

  const addList = (kind) => {
    const count = lists.filter((l) => listKind(l) === kind).length + 1;
    const name = `${kind === "bullet" ? "Bullet" : "Numbered"} list ${count}`;
    const list = { id: `list-${Date.now()}`, kind, name, levels: makeListLevels(kind) };
    setLists([...lists, list]);
    setOpenId(list.id);
  };
  const deleteList = (id) => {
    setLists(lists.filter((l) => l.id !== id));
    paragraphFormats.forEach((p) => {
      if (p.bulletListId === id) updateParagraphFormat(p.id, { bulletListId: null });
      if (p.numberListId === id) updateParagraphFormat(p.id, { numberListId: null });
    });
    setOpenId(null);
  };

  return (
    <>
      <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1 mt-3">Lists</p>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
        {lists.map((l) => (
          <button
            key={l.id}
            type="button"
            className={`ev-btn-small ${l.id === openId ? "ev-btn-blue-fill-round" : "ev-btn-border-square ev-message-card2 ev-card-3"}`}
            onClick={() => setOpenId(l.id === openId ? null : l.id)}
          >
            <i className={`bi ${listKind(l) === "bullet" ? "bi-list-ul" : "bi-list-ol"} me-1`}></i>
            {l.name}
          </button>
        ))}
        <button type="button" className="ev-btn-blue-fill-round ev-btn-small d-flex align-items-center gap-2" onClick={() => addList("bullet")}>
          <i className="bi bi-plus-circle-fill ev-fs-10"></i> New bullet list
        </button>
        <button type="button" className="ev-btn-blue-fill-round ev-btn-small d-flex align-items-center gap-2" onClick={() => addList("number")}>
          <i className="bi bi-plus-circle-fill ev-fs-10"></i> New numbered list
        </button>
      </div>

      {openList && (
        <ListEditor
          key={openList.id}
          list={openList}
          onChange={(next) => setLists(lists.map((l) => (l.id === next.id ? next : l)))}
          onDelete={() => deleteList(openList.id)}
        />
      )}

      <p className="ev-bold-reg ev-fs-12 ev-reg-p-dark mb-1">Link lists to paragraph styles</p>
      <div className="row g-2 ev-fs-11 ev-reg-p-dark mb-1">
        <div className="col-4"></div>
        <div className="col-4">Bullet list (-)</div>
        <div className="col-4">Numbered list (1.)</div>
      </div>
      {paragraphFormats.map((p) => (
        <div key={p.id} className="row g-2 align-items-center ev-fs-13 ev-reg-p-dark mb-2">
          <label className="col-4">{p.name}</label>
          <div className="col-4">
            <LabeledDropdown
              value={p.bulletListId || ""}
              options={linkOptions("bullet")}
              onChange={(val) => updateParagraphFormat(p.id, { bulletListId: val || null })}
            />
          </div>
          <div className="col-4">
            <LabeledDropdown
              value={p.numberListId || ""}
              options={linkOptions("number")}
              onChange={(val) => updateParagraphFormat(p.id, { numberListId: val || null })}
            />
          </div>
        </div>
      ))}
    </>
  );
};

const CellAlignmentButton = ({ vertical, horizontal, onChangeVertical, onChangeHorizontal }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const opts = [
    { v: "top",    h: "left",   icon: "bi-text-left",   title: "Align Top Left" },
    { v: "top",    h: "center", icon: "bi-text-center", title: "Align Top Center" },
    { v: "top",    h: "right",  icon: "bi-text-right",  title: "Align Top Right" },
    { v: "center", h: "left",   icon: "bi-text-left",   title: "Align Center Left" },
    { v: "center", h: "center", icon: "bi-text-center", title: "Align Center Center" },
    { v: "center", h: "right",  icon: "bi-text-right",  title: "Align Center Right" },
    { v: "bottom", h: "left",   icon: "bi-text-left",   title: "Align Bottom Left" },
    { v: "bottom", h: "center", icon: "bi-text-center", title: "Align Bottom Center" },
    { v: "bottom", h: "right",  icon: "bi-text-right",  title: "Align Bottom Right" },
  ];

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setOpen(!open)}
        title="Cell Alignment"
      >
        <i className="bi bi-grid-3x3"></i>
      </button>

      {open && (
        <div className="ev-message-card2 ev-card-3 position-absolute z-1000 p-2 rfm-popover-right" style={{ minWidth: "auto", width: "fit-content" }}>
          <p className="ev-fs-10 fw-bold mb-1 ev-reg-p-dark">Cell Alignment</p>
          <div className="d-inline-grid" style={{ gridTemplateColumns: "repeat(3, 26px)", gap: "2px" }}>
            {opts.map(({ v, h, icon, title }) => {
              const isActive = vertical === v && horizontal === h;
              return (
                <button
                  key={`${v}-${h}`}
                  type="button"
                  title={title}
                  className={`ev-btn-border-square d-flex align-items-${v === "top" ? "start" : v === "bottom" ? "end" : "center"} justify-content-center ${isActive ? "ev-btn-blue-fill-round" : "ev-message-card2 ev-card-3"}`}
                  style={{ width: 26, height: 26, padding: "2px 0", margin: 0 }}
                  onClick={() => {
                    onChangeVertical(v);
                    onChangeHorizontal(h);
                  }}
                >
                  <i className={`bi ${icon}`} style={{ fontSize: 11, lineHeight: 1 }} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const RefStyleButton = ({ refStyle, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const update = (patch) => onChange({ ...refStyle, ...patch });

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setOpen(!open)}
        title="Reference Text Style"
      >
        <i className="bi bi-fonts ev-fs-11"></i>
      </button>

      {open && (
        <div
          className="ev-message-card2 ev-card-3 position-absolute z-1000 p-2 rfm-popover-ref-style"
        >
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">Reference Text Style</p>
          <div className="d-flex align-items-center gap-1">
            <div className="rfm-ref-font-col flex-shrink-0">
              <CustomDropdown
                value={refStyle?.fontFamily || "Arial"}
                options={fontOptions}
                className="w-100"
                onChange={(val) => update({ fontFamily: val })}
                isFontSelector={true}
              />
            </div>
            <FontSizeInput
              value={refStyle?.fontSize || 10}
              onChange={(val) => update({ fontSize: val })}
            />
            <button
              type="button"
              className={`ev-btn-small ev-message-card2 ev-card-3 me-1 ev-btn-border-square ${refStyle?.bold ? 'ev-bold-large list-element-selected' : 'ev-reg'}`}
              onClick={() => update({ bold: !refStyle?.bold })}
            >B</button>
            <button
              type="button"
              className={`ev-btn-small ev-message-card2 ev-card-3 me-1 ev-btn-border-square ${refStyle?.italic ? 'ev-bold-large list-element-selected' : 'ev-reg'}`}
              onClick={() => update({ italic: !refStyle?.italic })}
              style={{ fontStyle: "italic" }}
            >I</button>
            <ColorPickerButton
              value={refStyle?.color || "#000000"}
              onChange={(color) => update({ color })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SPECIAL_INDENT_OPTIONS = [
  { value: "none",      label: "None" },
  { value: "firstLine", label: "First line" },
  { value: "hanging",   label: "Hanging" },
];

const SpecialIndentDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const current = SPECIAL_INDENT_OPTIONS.find((o) => o.value === value) || SPECIAL_INDENT_OPTIONS[0];

  return (
    <div className="position-relative w-100" ref={ref}>
      <button
        type="button"
        className="d-flex align-items-center justify-content-between formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 w-100"
        onClick={() => setOpen(!open)}
      >
        <span className="ev-fs-11 ev-reg-p-dark flex-grow-1 text-start ps-1">{current.label}</span>
        <i className="bi bi-chevron-down ev-fs-11 opacity-75 pe-1"></i>
      </button>
      {open && (
        <div className="ev-card-4 position-absolute p-2 rfm-special-dropdown rfm-popover-right ">
          {SPECIAL_INDENT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`ev-btn-menu-action mb-1 ${value === opt.value ? 'active' : ''}`}
              onClick={() => { onChange(opt.value); setOpen(false); }}
            >
              <span className="ev-fs-11">{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SpacingPopoverButton = ({ format, onChange, hideLineSpacing, hidden }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const update = (patch) => onChange({ ...format, ...patch });

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (hidden) return null;

  return (
    <div className="position-relative d-inline-block" ref={ref}>
      <button
        type="button"
        className="ev-btn-small ev-message-card2 ev-card-3 ms-1 ev-btn-border-square"
        onClick={() => setOpen(!open)}
        title="Spacing & Indentation"
      >
        <i className="bi bi-distribute-vertical"></i>
      </button>

      {open && (
        <div className="ev-message-card2 ev-card-3 position-absolute z-1000 p-3 rfm-popover-right rfm-spacing-indent-popover">

          {/* INDENTATION SECTION */}
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">Indentation</p>

          <div className="row g-3 mb-2">
            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Left</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={format.indentLeft ?? 0}
                    step={0.1}
                    unit="cm"
                    min={-55.87}
                    max={55.87}
                    showRangeWarning
                    onChange={(val) => update({ indentLeft: val })}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Right</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={format.indentRight ?? 0}
                    step={0.1}
                    unit="cm"
                    min={-55.87}
                    max={55.87}
                    showRangeWarning
                    onChange={(val) => update({ indentRight: val })}
                  />
                </div>
              </div>
            </div>

            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Special</span>
                <div className="rfm-popover-input-col">
                  <SpecialIndentDropdown
                    value={format.indentSpecial ?? "none"}
                    onChange={(val) => {
                      const patch = { indentSpecial: val };
                      if ((val === "firstLine" || val === "hanging") && (!format.indentBy || format.indentBy === 0)) {
                        patch.indentBy = 1.27;
                      } else if (val === "none") {
                        patch.indentBy = 0;
                      }
                      update(patch);
                    }}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <span className="ev-fs-11 ev-reg-p-dark me-2">By</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={format.indentBy ?? 0}
                    step={0.1}
                    unit="cm"
                    min={-55.87}
                    max={55.87}
                    showRangeWarning
                    onChange={(val) => update({ indentBy: val })}
                  />
                </div>
              </div>
            </div>
          </div>

          <hr className="my-2" />

          {/* SPACING SECTION */}
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">Spacing</p>

          <div className="row g-3">
            <div className="col-6">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Space Before</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={format.spaceBefore ?? 0}
                    step={1}
                    unit="pt"
                    min={0}
                    onChange={(val) => update({ spaceBefore: val })}
                  />
                </div>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <span className="ev-fs-11 ev-reg-p-dark me-2">Space After</span>
                <div className="rfm-popover-input-col">
                  <NumberStepperInput
                    value={format.spaceAfter ?? 12}
                    step={1}
                    unit="pt"
                    min={0}
                    onChange={(val) => update({ spaceAfter: val })}
                  />
                </div>
              </div>
            </div>

            {!hideLineSpacing && (
              <div className="col-6">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="ev-fs-11 ev-reg-p-dark me-2">Line Spacing</span>
                  <div className="rfm-popover-input-col">
                    <NumberStepperInput
                      value={Number(format.lineSpacing ?? 1.5)}
                      step={0.1}
                      min={0.5}
                      onChange={(val) => update({ lineSpacing: val })}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <hr className="my-2" />

          {/* INLINE PREVIEW — static styling via existing classes, dynamic format values inline (unavoidable) */}
          <p className="ev-fs-10 fw-bold mb-2 ev-reg-p-dark">Preview</p>

          <div className="ev-card-3 border rounded p-2 rfm-spacing-preview">
            <div className="ev-fs-10 ev-reg-p-dark opacity-50 rfm-spacing-preview-prev">
              Previous paragraph previous paragraph previous paragraph previous paragraph
            </div>
            <p
              className="ev-fs-10 rfm-spacing-preview-paragraph"
              style={{
                marginTop: `${format.spaceBefore || 0}pt`,
                marginRight: `${format.indentRight || 0}cm`,
                marginBottom: `${format.spaceAfter || 0}pt`,
                marginLeft: `${format.indentLeft || 0}cm`,
                lineHeight: Number(format.lineSpacing ?? 1.5),
                textIndent: format.indentSpecial === 'firstLine'
                  ? `${format.indentBy || 0}cm`
                  : format.indentSpecial === 'hanging'
                    ? `-${format.indentBy || 0}cm`
                    : '0',
                paddingLeft: format.indentSpecial === 'hanging' ? `${format.indentBy || 0}cm` : '0',
                color: format.color || 'inherit',
                fontWeight: format.bold ? 'bold' : 'normal',
                fontStyle: format.italic ? 'italic' : 'normal',
              }}
            >
              Sample paragraph showing how your formatting choices look. This text wraps to demonstrate line spacing and indentation.
            </p>
            <div className="ev-fs-10 ev-reg-p-dark opacity-50 rfm-spacing-preview-next">
              Following paragraph following paragraph following paragraph following paragraph
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const defaultFormat = {
  fontFamily: "Poppins Semibold",
  fontSize: 13,
  bold: false,
  italic: false,
  underline: "none",
  color: "#000000",
  lineSpacing: 1.5,
  spaceBefore: 0,
  spaceAfter: 12,
  indentLeft: 0,
  indentRight: 0,
  indentSpecial: "none",
  indentBy: 0,
};

const DEFAULT_TOC_CONFIG = {
  text: { ...defaultFormat, fontFamily: "Arial", fontSize: 12 },
  styles: [{ id: "toc-default", name: "TOC (Default)", level: 0, ...defaultFormat }],
  lotStyle: { ...defaultFormat },
  lofStyle: { ...defaultFormat },
  design: "design-1",
  tocLevelNum: 1,
  tocNumberSpacing: 0,
  tocShowDots: true,
};

export const withTocDefaults = (formatJson) => {
  if (!formatJson) return formatJson;
  const toc = formatJson.tables?.toc || {};
  const styles = Array.isArray(toc.styles) && toc.styles.length > 0
    ? toc.styles.map((s) => ({ ...defaultFormat, ...s }))
    : DEFAULT_TOC_CONFIG.styles.map((s) => ({ ...s }));
  return {
    ...formatJson,
    tables: {
      ...(formatJson.tables || {}),
      toc: {
        ...toc,
        text: { ...DEFAULT_TOC_CONFIG.text, ...(toc.text || {}) },
        styles,
        lotStyle: { ...defaultFormat, ...(toc.lotStyle || {}) },
        lofStyle: { ...defaultFormat, ...(toc.lofStyle || {}) },
        design: toc.design || DEFAULT_TOC_CONFIG.design,
        tocLevelNum: toc.tocLevelNum ?? DEFAULT_TOC_CONFIG.tocLevelNum,
        tocNumberSpacing: toc.tocNumberSpacing ?? DEFAULT_TOC_CONFIG.tocNumberSpacing,
        tocShowDots: toc.tocShowDots ?? DEFAULT_TOC_CONFIG.tocShowDots,
      },
    },
  };
};

const ReportFormattingModal = ({ onClose, onSave, templateId, initialConfig, canSave = false }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [lists, setLists] = useState([]);
  const [paragraphFormats, setParagraphFormats] = useState([
    {
      id: "default",
      name: "Paragraph (Default)",
      ...defaultFormat,
      alignment: "justify",
      backgroundColor: "#FFFFFF",
      borders: "none",
    }
  ]);
  const [captionFormat, setCaptionFormat] = useState({ ...defaultFormat });

  const [headerFormats, setHeaderFormats] = useState([
    { id: "default", name: "Header (Default)", level: 0, ...defaultFormat, backgroundColor: "#FFFFFF" },
  ]);

  // NEW: Agent Heading format state — bold, single-line, config-driven style.
  // Mirrors the backend's default of bold=True while remaining fully overridable.
  const [agentHeadingFormat, setAgentHeadingFormat] = useState({ ...defaultFormat, bold: true });

  const [newHeaderLevel, setNewHeaderLevel] = useState(1);
  const [previewDesign, setPreviewDesign] = useState(null); // Track which design is being previewed

  // Table & TOC States
  const [generalTableTextFormat, setGeneralTableTextFormat] = useState({ ...defaultFormat });
  const [generalTableHeaderFormat, setGeneralTableHeaderFormat] = useState({ ...defaultFormat });
  const [generalTableDesign, setGeneralTableDesign] = useState("design-1");
  const [abbreviationUseTableDesign, setAbbreviationUseTableDesign] = useState(false);
  const [generalTableBorderColor, setGeneralTableBorderColor] = useState(null);
  // Which colour the single picker beside Table Design is editing.
  const [tableColorMode, setTableColorMode] = useState("header");
  const [tocTableTextFormat, setTocTableTextFormat] = useState({ ...defaultFormat }); // legacy fallback
  const [tocStyleFormats, setTocStyleFormats] = useState([
    { id: "toc-default", name: "TOC (Default)", level: 0, ...defaultFormat },
  ]);
  const [lotStyleFormat, setLotStyleFormat] = useState({ ...defaultFormat });
  const [lofStyleFormat, setLofStyleFormat] = useState({ ...defaultFormat });
  const [tocTableDesign, setTocTableDesign] = useState("design-1");
  const [tableCaptionPlacement, setTableCaptionPlacement] = useState("above");
  const [tocLevelOpen, setTocLevelOpen] = useState(false);
  const tocLevelRef = useRef(null);

  // Formatting States
  const [pageSize, setPageSize] = useState("a4");
  const [marginPreset, setMarginPreset] = useState("normal");
  const [marginTop, setMarginTop] = useState(1.0);
  const [marginBottom, setMarginBottom] = useState(1.0);
  const [marginLeft, setMarginLeft] = useState(1.0);
  const [marginRight, setMarginRight] = useState(1.0);
  const [marginGutter, setMarginGutter] = useState(0.0);
  const [marginGutterPosition, setMarginGutterPosition] = useState("left");
  const [marginMirrored, setMarginMirrored] = useState(false);

  const handleMarginPresetChange = (preset) => {
    setMarginPreset(preset);
    if (preset !== "custom" && MARGIN_PRESETS[preset]) {
      const p = MARGIN_PRESETS[preset];
      setMarginTop(p.top); setMarginBottom(p.bottom);
      setMarginLeft(p.left); setMarginRight(p.right);
      setMarginGutter(p.gutter); setMarginGutterPosition(p.gutterPosition);
      setMarginMirrored(p.mirrored);
    } else if (preset === "custom") {
      // Custom keeps the current values as a starting point but drops preset-only flags
      setMarginMirrored(false);
    }
  };

  // Editing any margin field turns the selection into Custom (see handleMarginPresetChange)
  const switchMarginToCustom = () => handleMarginPresetChange("custom");

  const resolvedMargins = marginPreset !== "custom" && MARGIN_PRESETS[marginPreset]
    ? MARGIN_PRESETS[marginPreset]
    : { top: marginTop, bottom: marginBottom, left: marginLeft, right: marginRight, gutter: marginGutter, gutterPosition: marginGutterPosition, mirrored: marginMirrored };

  const [numberingFormat, setNumberingFormat] = useState("none");
  const [headingNumberPeriod, setHeadingNumberPeriod] = useState(false);
  const [numberingStartChar, setNumberingStartChar] = useState("A"); 
  
  const [citationFormat, setCitationFormat] = useState("apa");
  const [citationInlineStyle, setCitationInlineStyle] = useState("numeric");
  const [inlineCitationFormat, setInlineCitationFormat] = useState({ ...defaultFormat, fontSize: 10 });
  const [tableRefFormat, setTableRefFormat] = useState({ ...defaultFormat, fontSize: 10 });
  const [figureRefFormat, setFigureRefFormat] = useState({ ...defaultFormat, fontSize: 10 });
  const [figureCaptionPlacement, setFigureCaptionPlacement] = useState("above");
  // const [lineSpacing, setLineSpacing] = useState(1.5);
  // const [paragraphSpacing, setParagraphSpacing] = useState(12);
  const [showTocPreview, setShowTocPreview] = useState(false);

  const [previewFrontPagePath, setPreviewFrontPagePath] = useState(null);
  const [numberingStartValue, setNumberingStartValue] = useState(null);

  const [imageFootNotesFormat, setImageFootNotesFormat] = useState({ ...defaultFormat, fontSize: 8, lineSpacing: 1.15, spaceAfter: 12, spaceBefore: 0 });
  const [generalTableFooterFormat, setGeneralTableFooterFormat] = useState({ ...defaultFormat, fontSize: 9, lineSpacing: 1, spaceAfter: 0, spaceBefore: 0 });
  const [tocLevelNum, setTocLevelNum] = useState(1);
  const [tocNumberSpacing, setTocNumberSpacing] = useState(0);
  const [tocShowDots, setTocShowDots] = useState(true);
  const [includeLists, setIncludeLists] = useState({ toc: true, lot: true, lof: true, abbreviations: true });
  const [bibIndent, setBibIndent] = useState(0.5);
  const [bibLeftMargin, setBibLeftMargin] = useState(0);
  const [showBibPreview, setShowBibPreview] = useState(false);
  const [refStyle, setRefStyle] = useState({ fontFamily: "Arial", fontSize: 10, bold: false, italic: false, underline: "none", eastAsiaFont: null, color: "#000000", lineSpacing: 1.0, spaceBefore: 0, spaceAfter: 0, indentLeft: 0, indentRight: 0, indentSpecial: "none", indentBy: 0 });
  const [tableBulletIndentPt, setTableBulletIndentPt] = useState(11);
  const [tableCellVerticalAlign, setTableCellVerticalAlign] = useState("center");
  const [tableCellHorizontalAlign, setTableCellHorizontalAlign] = useState("left");
  const [tableHeaderVerticalAlign, setTableHeaderVerticalAlign] = useState("center");
  const [tableHeaderHorizontalAlign, setTableHeaderHorizontalAlign] = useState("left");

  const [figureUseHeadingNum, setFigureUseHeadingNum] = useState(false);
  const [figureHeadingLevel,  setFigureHeadingLevel]  = useState(1);
  const [figureConnector,     setFigureConnector]     = useState("-");
  // Non-breaking toggle: the report writes every "-" of the connector as a non-breaking hyphen
  const [figureConnectorNb,   setFigureConnectorNb]   = useState(false);

  const [tableUseHeadingNum,  setTableUseHeadingNum]  = useState(false);
  const [tableHeadingLevel,   setTableHeadingLevel]   = useState(1);
  const [tableConnector,      setTableConnector]      = useState("-");
  const [tableConnectorNb,    setTableConnectorNb]    = useState(false);
  const [figureSuffix,        setFigureSuffix]        = useState(".");
  const [tableSuffix,         setTableSuffix]         = useState(".");
  const [quotationFormat,     setQuotationFormat]     = useState({
    fontFamily:    "Calibri Light",
    fontSize:      11,
    bold:          false,
    italic:        true,
    underline:     "none",
    color:         "#00B0F0",
    alignment:     "center",
    lineSpacing:   1.15,
    spaceBefore:   6,
    spaceAfter:    6,
    borderColor:   "#00B0F0",
    indentLeft:    0,
    indentRight:   0,
    indentSpecial: "none",
    indentBy:      0,
  });
  const headers = {
    Authorization: GetCookie("access_token"),
  };

  useEffect(() => {
    if (initialConfig) {
      const cfg = initialConfig.config || initialConfig;

      // PARAGRAPH FORMATS
      // Final canonical format: cfg.paragraph = [default, custom level styles]
      // Legacy fallback: cfg.paragraph as object + cfg.paragraphStyles array
      const normalizeParagraphEntry = (p, idx, isDefault = false) => ({
        id: p?.id || (isDefault ? "default" : `paragraph-${idx}`),
        name: p?.name || (isDefault ? "Paragraph (Default)" : `Paragraph (Style ${idx})`),
        ...defaultFormat,
        alignment: "justify",
        backgroundColor: "#FFFFFF",
        borders: isDefault ? "none" : "outside",
        ...p,
      });

      if (Array.isArray(cfg.paragraph) && cfg.paragraph.length > 0) {
        setParagraphFormats(
          cfg.paragraph.map((p, idx) => {
            const isDefault = (p?.id || "").toLowerCase() === "default" || p?.level === 0 || idx === 0;
            return normalizeParagraphEntry(p, idx, isDefault);
          })
        );
      } else if (cfg.paragraph && typeof cfg.paragraph === "object") {
        const defaultPara = normalizeParagraphEntry(cfg.paragraph, 0, true);
        const customStyles = Array.isArray(cfg.paragraphStyles)
          ? cfg.paragraphStyles.map((p, idx) => normalizeParagraphEntry(p, idx + 1, false))
          : [];
        setParagraphFormats([defaultPara, ...customStyles]);
      } else if (Array.isArray(cfg.paragraphs) && cfg.paragraphs.length > 0) {
        setParagraphFormats(
          cfg.paragraphs.map((p, idx) => normalizeParagraphEntry(p, idx, idx === 0))
        );
      } else {
        setParagraphFormats([
          {
            id: "default",
            name: "Paragraph (Default)",
            ...defaultFormat,
            alignment: "justify",
            backgroundColor: "#FFFFFF",
            borders: "none",
          },
        ]);
      }

      if (cfg.caption) setCaptionFormat({ ...defaultFormat, ...cfg.caption });
      if (Array.isArray(cfg.headers) && cfg.headers.length > 0) {
        setHeaderFormats(
          cfg.headers.map((h, idx) => ({
              id: h.id || `header-${idx}`,
              name: h.name || `Header (Level ${h.level ?? idx})`,
              ...defaultFormat,
              backgroundColor: "#FFFFFF",
              ...h,
          }))
        );
      }

      // NEW: Agent Heading config load — falls back to bold=true default when
      // the section is missing, matching the backend's fallback behavior.
      if (cfg.agentHeading) {
        setAgentHeadingFormat({ ...defaultFormat, bold: true, ...cfg.agentHeading });
      }

      if (cfg.tables?.general?.text) setGeneralTableTextFormat({ ...defaultFormat, ...cfg.tables.general.text });
      if (cfg.tables?.general?.header) setGeneralTableHeaderFormat({ ...defaultFormat, ...cfg.tables.general.header });
      if (cfg.tables?.general?.design) setGeneralTableDesign(cfg.tables.general.design);
      if (cfg.tables?.general?.abbreviationUseTableDesign !== undefined) setAbbreviationUseTableDesign(cfg.tables.general.abbreviationUseTableDesign);
      if (cfg.tables?.general?.borderColor !== undefined) setGeneralTableBorderColor(cfg.tables.general.borderColor);
      if (cfg.tables?.general?.captionPlacement) setTableCaptionPlacement(cfg.tables.general.captionPlacement);
      if (cfg.tables?.toc?.text) setTocTableTextFormat({ ...defaultFormat, ...cfg.tables.toc.text });
      if (Array.isArray(cfg.tables?.toc?.styles) && cfg.tables.toc.styles.length > 0) {
        setTocStyleFormats(
          cfg.tables.toc.styles.map((t, idx) => {
            const merged = { ...defaultFormat, ...t };
            // Force id/name/level to consistent positions
            const { id, name, level, ...rest } = merged;
            return {
              id: id || `toc-${idx}`,
              name: name || (t.level === 0 ? "TOC (Default)" : `TOC (Style ${t.level})`),
              level: level ?? idx,
              ...rest,
            };
          })
        );
      }
      if (cfg.tables?.toc?.design) setTocTableDesign(cfg.tables.toc.design);
      if (cfg.tables?.toc?.lotStyle) setLotStyleFormat({ ...defaultFormat, ...cfg.tables.toc.lotStyle });
      if (cfg.tables?.toc?.lofStyle) setLofStyleFormat({ ...defaultFormat, ...cfg.tables.toc.lofStyle });

      if (cfg.pageSize) setPageSize(cfg.pageSize);
      if (cfg.margins) {
        const m = cfg.margins;
        if (m.preset) {
          setMarginPreset(m.preset);
          if (m.preset !== "custom" && MARGIN_PRESETS[m.preset]) {
            const p = MARGIN_PRESETS[m.preset];
            setMarginTop(p.top); setMarginBottom(p.bottom);
            setMarginLeft(p.left); setMarginRight(p.right);
            setMarginGutter(p.gutter); setMarginGutterPosition(p.gutterPosition);
            setMarginMirrored(p.mirrored);
          } else {
            if (m.top !== undefined) setMarginTop(m.top);
            if (m.bottom !== undefined) setMarginBottom(m.bottom);
            if (m.left !== undefined) setMarginLeft(m.left);
            if (m.right !== undefined) setMarginRight(m.right);
            if (m.gutter !== undefined) setMarginGutter(m.gutter);
            if (m.gutterPosition) setMarginGutterPosition(m.gutterPosition);
            setMarginMirrored(false); // custom never mirrors; only the Mirrored preset does
          }
        }
      }
      setHeadingNumberPeriod(cfg.headingNumberPeriod === true);
      if (cfg.numberingFormat) {
        setNumberingFormat(cfg.numberingFormat);
 
      if (cfg.numberingFormat !== "none") {
      setNumberingStartChar(cfg.numberingFormat);
      }

        if (cfg.numberingStartValue !== undefined) setNumberingStartValue(cfg.numberingStartValue);


      }
      if (cfg.citationFormat) {
        if (typeof cfg.citationFormat === 'object') {
          setCitationFormat(cfg.citationFormat.style || "apa");
           
          const inlineConf = cfg.citationFormat.inline || {};
           if (inlineConf.superscript) {
             setCitationInlineStyle("superscript");
           } else if (inlineConf.subscript) {
             setCitationInlineStyle("subscript");
           } else {
             setCitationInlineStyle("numeric"); 
           }
          setInlineCitationFormat({
            ...defaultFormat,
            fontFamily: inlineConf.fontFamily || defaultFormat.fontFamily,
            fontSize: inlineConf.fontSize ?? 10,
            bold: inlineConf.bold || false,
            italic: inlineConf.italic || false,
            underline: inlineConf.underline || "none",
            color: inlineConf.fontColor || "#000000",
            eastAsiaFont: inlineConf.eastAsiaFont || null,
          });
           
        } else {
          setCitationFormat(cfg.citationFormat);
          setCitationInlineStyle("numeric");
        }
      }
      if (cfg.figureCaptionPlacement) setFigureCaptionPlacement(cfg.figureCaptionPlacement);
      // if (cfg.lineSpacing) setLineSpacing(Number(cfg.lineSpacing));
      // if (cfg.spaceAfter!== undefined) setParagraphSpacing(Number(cfg.spaceAfter));
      if (cfg.imageFootNotes) setImageFootNotesFormat({ ...defaultFormat, ...cfg.imageFootNotes });
      if (Array.isArray(cfg.lists)) setLists(cfg.lists);
      if (cfg.tables?.general?.footer) setGeneralTableFooterFormat({ ...defaultFormat, ...cfg.tables.general.footer });
      if (cfg.tables?.toc?.tocLevelNum !== undefined) setTocLevelNum(cfg.tables.toc.tocLevelNum);
      if (cfg.tables?.toc?.tocNumberSpacing !== undefined) setTocNumberSpacing(Number(cfg.tables.toc.tocNumberSpacing) || 0);
      setTocShowDots(cfg.tables?.toc?.tocShowDots !== false);
      setIncludeLists({
        toc: cfg.tables?.toc?.showToc !== false,
        lot: cfg.tables?.toc?.showLot !== false,
        lof: cfg.tables?.toc?.showLof !== false,
        abbreviations: cfg.tables?.toc?.showAbbreviations !== false,
      });
      if (cfg.bibliography) {
        const bib = cfg.bibliography;
        const rs = bib.ref_style || {};
        if ((rs.indent ?? bib.indent) !== undefined) setBibIndent(rs.indent ?? bib.indent);
        if ((rs.leftMargin ?? bib.leftMargin) !== undefined) setBibLeftMargin(rs.leftMargin ?? bib.leftMargin);
        setRefStyle({
          fontFamily:   rs.fontFamily   || "Arial",
          fontSize:     rs.fontSize     || 10,
          bold:         rs.bold         || false,
          italic:       rs.italic       || false,
          underline:    rs.underline    || "none",
          eastAsiaFont: rs.eastAsiaFont || null,
          color:        rs.color        || "#000000",
          lineSpacing:  rs.lineSpacing  ?? bib.lineSpacing ?? 1.0,
          spaceBefore:  rs.spaceBefore  ?? 0,
          spaceAfter:   rs.spaceAfter   ?? bib.entrySpacing ?? 0,
        });
      }
      if (cfg.tables?.general?.bulletIndentPt !== undefined) setTableBulletIndentPt(cfg.tables.general.bulletIndentPt);
      if (cfg.tables?.general?.cellVerticalAlign) setTableCellVerticalAlign(cfg.tables.general.cellVerticalAlign);
      if (cfg.tables?.general?.cellHorizontalAlign) setTableCellHorizontalAlign(cfg.tables.general.cellHorizontalAlign);
      // Older configs have no header alignment; they fall back to the body cell alignment
      const headerVAlign = cfg.tables?.general?.headerVerticalAlign || cfg.tables?.general?.cellVerticalAlign;
      const headerHAlign = cfg.tables?.general?.headerHorizontalAlign || cfg.tables?.general?.cellHorizontalAlign;
      if (headerVAlign) setTableHeaderVerticalAlign(headerVAlign);
      if (headerHAlign) setTableHeaderHorizontalAlign(headerHAlign);
      if (cfg.figureNumbering) {
        setFigureUseHeadingNum(cfg.figureNumbering.useHeadingNumber ?? false);
        setFigureHeadingLevel(cfg.figureNumbering.headingLevel ?? 1);
        setFigureConnector(cfg.figureNumbering.connector ?? "-");
        setFigureConnectorNb(cfg.figureNumbering.nonBreakingConnector ?? false);
        setFigureSuffix(cfg.figureNumbering.suffix ?? ".");
      }
      if (cfg.tableNumbering) {
        setTableUseHeadingNum(cfg.tableNumbering.useHeadingNumber ?? false);
        setTableHeadingLevel(cfg.tableNumbering.headingLevel ?? 1);
        setTableConnector(cfg.tableNumbering.connector ?? "-");
        setTableConnectorNb(cfg.tableNumbering.nonBreakingConnector ?? false);
        setTableSuffix(cfg.tableNumbering.suffix ?? ".");
      }
      if (cfg.tableRefStyle) setTableRefFormat({ ...defaultFormat, ...cfg.tableRefStyle });
      if (cfg.figureRefStyle) setFigureRefFormat({ ...defaultFormat, ...cfg.figureRefStyle });
      if (cfg.quotation) {
        setQuotationFormat((prev) => ({ ...prev, ...cfg.quotation }));
      }
    }
  }, [initialConfig]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tocLevelRef.current && !tocLevelRef.current.contains(event.target)) {
        setTocLevelOpen(false);
      }
    };
    if (tocLevelOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [tocLevelOpen]);

  useEffect(() => {
    const customHeaders = headerFormats.filter((h) => h.level > 0);
    const maxLevel = customHeaders.length > 0
      ? Math.max(...customHeaders.map((h) => h.level))
      : 1;
    if (tocLevelNum > maxLevel) {
      setTocLevelNum(maxLevel);
    }
  }, [headerFormats]);
  

  const updateHeaderFormat = (id, patch) => {
    setHeaderFormats((prev) =>
      prev.map((h) => (h.id === id ? { ...h, ...patch } : h))
    );
  };

  const updateParagraphFormat = (id, patch) => {
  setParagraphFormats((prev) =>
    prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
  );
  };

  const handleAddParagraphStyle = () => {
    const nextNumber = paragraphFormats.length;
    const newParagraph = {
      id: `paragraph-${nextNumber}`,
      name: `Paragraph (Style ${nextNumber})`,
      ...defaultFormat,
      alignment: "justify",
      backgroundColor: "#FFFFFF",
      borders: "outside",
    };
    setParagraphFormats((prev) => [...prev, newParagraph]);
  };

  const handleDeleteParagraph = (id) => {
  if (id === "default") return; // safety guard
    setParagraphFormats((prev) =>
      prev
        .filter((p) => p.id !== id)
        .map((p, idx) =>
          p.id === "default"
            ? p
            : { ...p, id: `paragraph-${idx}`, name: `Paragraph (Style ${idx})` }
        )
    );
  };

  const handleAddSingleHeader = () => {
    const userInput = parseInt(newHeaderLevel, 10);
    if (!userInput || userInput < 1 || userInput > 9) return;
    const backendLevel = userInput;
    if (headerFormats.some((h) => h.level === backendLevel)) {
      return; 
    }
    const newHeader = {
      ...defaultFormat,
      id: `header-level-${backendLevel}`,
      name: `Header (Level ${userInput})`,
      level: backendLevel,
      backgroundColor: "#FFFFFF",
    };
    
    setHeaderFormats((prev) => {
      const updated = [...prev, newHeader];
      return updated.sort((a, b) => a.level - b.level);
    });
  };

  const handleDeleteHeader = (id) => {
    setHeaderFormats((prev) =>
      prev.filter((h) => h.level === 0 || h.id !== id)
    );
  };

  // ── TOC style management ─────────────────────────────────────────
  const updateTocStyleFormat = (id, patch) => {
    setTocStyleFormats((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...patch } : t))
    );
  };

  const handleAddTocStyle = () => {
    const nextNumber = tocStyleFormats.length; // 0=default already exists, so next is 1, 2, ...
    const newStyle = {
      id: `toc-${nextNumber}`,
      name: `TOC (Style ${nextNumber})`,
      level: nextNumber,
      ...defaultFormat,
    };
    setTocStyleFormats((prev) => [...prev, newStyle]);
  };

  const handleDeleteTocStyle = (id) => {
    setTocStyleFormats((prev) => prev.filter((t) => t.level === 0 || t.id !== id));
  };


  const handleSave = () => {

    let inlineConfigPayload = {
      fontFamily: inlineCitationFormat.fontFamily,
      fontSize: inlineCitationFormat.fontSize,
      bold: inlineCitationFormat.bold,
      italic: inlineCitationFormat.italic,
      underline: inlineCitationFormat.underline || "none",
      fontColor: inlineCitationFormat.color,
      eastAsiaFont: inlineCitationFormat.eastAsiaFont || null,
    };
    if (citationInlineStyle === 'superscript') {
      inlineConfigPayload = { ...inlineConfigPayload, superscript: true };
    } else if (citationInlineStyle === 'subscript') {
      inlineConfigPayload = { ...inlineConfigPayload, subscript: true };
    }

    const normalizedParagraphs = paragraphFormats.map((p, idx) => {
      const isDefault = p.id === "default" || idx === 0;

      return {
        id: isDefault ? "default" : (p.id || `paragraph-${idx}`),
        name: p.name || (isDefault ? "Paragraph (Default)" : `Paragraph (Style ${idx})`),
      fontFamily: p.fontFamily,
      fontSize: p.fontSize,
      bold: p.bold,
      italic: p.italic,
      underline: p.underline || "none",
      eastAsiaFont: p.eastAsiaFont || null,
      color: p.color,
      lineSpacing: p.lineSpacing,
      spaceBefore: p.spaceBefore,
      spaceAfter: p.spaceAfter,
      alignment: p.alignment,
      indentLeft: p.indentLeft ?? 0,
      indentRight: p.indentRight ?? 0,
      indentSpecial: p.indentSpecial ?? "none",
      indentBy: p.indentBy ?? 0,
      bulletListId: p.bulletListId || null,
      numberListId: p.numberListId || null,
      // The backend reads listFormat; the linked list's levels are copied in here.
      listFormat: {
        bullet: lists.find((l) => l.id === p.bulletListId)?.levels || [],
        number: lists.find((l) => l.id === p.numberListId)?.levels || [],
      },
      ...(isDefault ? {} : {
        backgroundColor: p.backgroundColor,
        borders: p.borders || "outside",
        level: idx 
      }),
      };
    });

    const configPayload = {
      paragraph: normalizedParagraphs,
      lists,
      caption: captionFormat,
      headers: headerFormats,
      // NEW: Agent Heading persisted alongside headers so the backend's
      // _gen_agent_heading_style(self.json_l.get("agentHeading", {})) picks it up.
      agentHeading: agentHeadingFormat,
      tables: {
        general: {
          text: generalTableTextFormat,
          header: generalTableHeaderFormat,
          footer: generalTableFooterFormat,
          design: generalTableDesign,
          borderColor: generalTableBorderColor,
          abbreviationUseTableDesign: abbreviationUseTableDesign,
          captionPlacement: tableCaptionPlacement,
          bulletIndentPt: tableBulletIndentPt,
          cellVerticalAlign: tableCellVerticalAlign,
          cellHorizontalAlign: tableCellHorizontalAlign,
          headerVerticalAlign: tableHeaderVerticalAlign,
          headerHorizontalAlign: tableHeaderHorizontalAlign,
        },
        toc: {
          text: tocTableTextFormat,
          styles: tocStyleFormats,
          lotStyle: lotStyleFormat,
          lofStyle: lofStyleFormat,
          design: tocTableDesign,
          tocLevelNum: tocLevelNum,
          tocNumberSpacing: tocNumberSpacing,
          tocShowDots: tocShowDots,
          showToc: includeLists.toc,
          showLot: includeLists.lot,
          showLof: includeLists.lof,
          showAbbreviations: includeLists.abbreviations,
        },
      },
      numberGapPt: 0,
      pageSize,
      margins: {
        preset: marginPreset,
        top: resolvedMargins.top,
        bottom: resolvedMargins.bottom,
        left: resolvedMargins.left,
        right: resolvedMargins.right,
        gutter: resolvedMargins.gutter,
        gutterPosition: resolvedMargins.gutterPosition,
        mirrored: resolvedMargins.mirrored,
      },
      numberingFormat,
      numberingStartValue,
      headingNumberPeriod,
      citationFormat: {
          style: citationFormat,     
          inline: inlineConfigPayload 
      },
      figureCaptionPlacement,
      figureNumbering: {
        useHeadingNumber: figureUseHeadingNum,
        headingLevel: figureHeadingLevel,
        connector: figureConnector,
        nonBreakingConnector: figureConnectorNb,
        suffix: figureSuffix,
      },
      tableNumbering: {
        useHeadingNumber: tableUseHeadingNum,
        headingLevel: tableHeadingLevel,
        connector: tableConnector,
        nonBreakingConnector: tableConnectorNb,
        suffix: tableSuffix,
      },
      tableRefStyle: {
        fontFamily: tableRefFormat.fontFamily,
        fontSize: tableRefFormat.fontSize,
        bold: tableRefFormat.bold,
        italic: tableRefFormat.italic,
        underline: tableRefFormat.underline || "none",
        color: tableRefFormat.color,
        eastAsiaFont: tableRefFormat.eastAsiaFont || null,
      },
      figureRefStyle: {
        fontFamily: figureRefFormat.fontFamily,
        fontSize: figureRefFormat.fontSize,
        bold: figureRefFormat.bold,
        italic: figureRefFormat.italic,
        underline: figureRefFormat.underline || "none",
        color: figureRefFormat.color,
        eastAsiaFont: figureRefFormat.eastAsiaFont || null,
      },
      imageFootNotes: imageFootNotesFormat,
      bibliography: {
        ref_style: {
          ...refStyle,
          indent:     bibIndent,
          leftMargin: bibLeftMargin,
        },
      },
      quotation: {
        fontFamily:    quotationFormat.fontFamily,
        fontSize:      quotationFormat.fontSize,
        bold:          quotationFormat.bold,
        italic:        quotationFormat.italic,
        underline:     quotationFormat.underline || "none",
        color:         quotationFormat.color,
        alignment:     quotationFormat.alignment,
        lineSpacing:   quotationFormat.lineSpacing,
        spaceBefore:   quotationFormat.spaceBefore,
        spaceAfter:    quotationFormat.spaceAfter,
        borderColor:   quotationFormat.borderColor,
        indentLeft:    quotationFormat.indentLeft,
        indentRight:   quotationFormat.indentRight,
        indentSpecial: quotationFormat.indentSpecial,
        indentBy:      quotationFormat.indentBy,
      },
    };

   
    
    if (onSave) {
      onSave(configPayload);
    }
    
    onClose();
  };

  const getNextChar = (char, increment) => {
    return String.fromCharCode(char.charCodeAt(0) + increment);
  };


  const maxHeaderLevel = (() => {
    const customHeaders = headerFormats.filter((h) => h.level > 0);
    return customHeaders.length > 0 ? Math.max(...customHeaders.map((h) => h.level)) : 1;
  })();

  return createPortal(
    <>
      <div
        className="modal fade show d-block bg-dark bg-opacity-50"
        style={{ zIndex: "var(--num-1000000000)" }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "80vw" }}>
          <div className="modal-content ev-message-card2 ev-card-3 ev-radius-med d-flex flex-column vh-90 ev-radius-med">
            
            <div className="p-3 d-flex justify-content-between align-items-center flex-shrink-0">
              <p className="mb-0 ev-fs-18 ev-bold-med ev-reg-p-blue">
                Report Formatting
              </p>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              />
            </div>

            <div className="modal-body ev-fs-12 scrollable-element flex-grow-1 p-3" style={{ height: 'auto' }}>
              
              {error && (
                <div className="alert alert-danger py-1 px-2 mb-2">
                  {error}
                </div>
              )}

              {loading ? (
                <div className="d-flex justify-content-center py-4">
                  <span className="ev-reg-p-dark">Loading...</span>
                </div>
              ) : (
                <div className="row gx-5">
                  <div className="col-6">
                    <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1">
                      General
                    </p>

                    {/* ADD PARAGRAPH STYLE BUTTON */}
                    <div className="d-flex mb-2 ev-fs-13 align-items-center ev-reg-p-dark me-4">
                      <label className="col-4">Paragraph (Custom)</label>
                      <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-small d-flex align-items-center gap-2"
                        onClick={handleAddParagraphStyle}
                      >
                        <i className="bi bi-plus-circle-fill ev-fs-10"></i>
                        Add Paragraph Style
                      </button>
                    </div>

                    {/* RENDER ALL PARAGRAPH FORMATS */}
                    {paragraphFormats.map((paragraph) => (
                      <div
                        key={paragraph.id}
                        className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4"
                      >
                        <label className="col-4">{paragraph.name}</label>
                        <FormatControls
                          format={paragraph}
                          onChange={(updated) =>
                            updateParagraphFormat(paragraph.id, updated)
                          }
                          // fontFamilyWidth={paragraph.id === "default" ? "w-25" : "w-19"}
                        />
                        {paragraph.id === "default" ? (
                          <MoreOptionsDropdown>
                            <AlignmentDropdownButton
                              alignment={paragraph.alignment || "justify"}
                              onChange={(align) =>
                                updateParagraphFormat(paragraph.id, { alignment: align })
                              }
                            />
                            <UnderlineDropdownButton
                              underline={paragraph.underline || "none"}
                              onChange={(u) =>
                                updateParagraphFormat(paragraph.id, { underline: u })
                              }
                            />
                            <EastAsiaFontButton
                              value={paragraph.eastAsiaFont}
                              onChange={(val) =>
                                updateParagraphFormat(paragraph.id, { eastAsiaFont: val })
                              }
                            />
                          </MoreOptionsDropdown>
                        ) : (
                          <MoreOptionsDropdown>
                            <AlignmentDropdownButton
                              alignment={paragraph.alignment || "justify"}
                              onChange={(align) =>
                                updateParagraphFormat(paragraph.id, { alignment: align })
                              }
                            />
                            <UnderlineDropdownButton
                              underline={paragraph.underline || "none"}
                              onChange={(u) =>
                                updateParagraphFormat(paragraph.id, { underline: u })
                              }
                            />
                            <BorderDropdownButton
                              borders={paragraph.borders || "outside"}
                              onChange={(border) =>
                                updateParagraphFormat(paragraph.id, { borders: border })
                              }
                            />
                            <ColorPickerButton
                              value={paragraph.backgroundColor || "#FFFFFF"}
                              onChange={(color) =>
                                updateParagraphFormat(paragraph.id, { backgroundColor: color })
                              }
                              icon={<i className="bi bi-paint-bucket"></i>}
                            />
                            <EastAsiaFontButton
                              value={paragraph.eastAsiaFont}
                              onChange={(val) =>
                                updateParagraphFormat(paragraph.id, { eastAsiaFont: val })
                              }
                            />
                          </MoreOptionsDropdown>
                        )}

                        {/* Delete button - only show for non-default */}
                        {paragraph.id !== "default" && (
                          <button
                            type="button"
                            className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 ms-1 py-1"
                            onClick={() => handleDeleteParagraph(paragraph.id)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    ))}

                    <p className="ev-bold-reg ev-fs-14 ev-med-p-blue  mb-1">
                      Headers
                    </p>
                    <div className="d-flex mb-2 ev-fs-13 align-items-center ev-reg-p-dark me-4">
                      <label className="col-4">Header (Custom)</label>
                      <div className="d-flex align-items-center ev-radius-small ev-message-card2 ev-card-3 ev-btn-med px-2 w-25">
                        <span className="ev-fs-13">Level</span>
                        <input
                          type="number"
                          min="1"
                          max="9"
                          className="ev-btn-border-square p-0 text-center ev-message-card2 ev-card-3 fw-bold ms-auto w-30"
                          style={{ outline: "none", backgroundColor: "transparent" }}
                          value={newHeaderLevel}
                          onChange={(e) => setNewHeaderLevel(e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-small d-flex align-items-center gap-2 ms-1"
                        onClick={handleAddSingleHeader}
                      >
                        <i className="bi bi-plus-circle-fill ev-fs-10"></i>
                        Add Header
                      </button>
                    </div>

                    {headerFormats.map((header) => (
                      <div key={header.id} className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                        <label className="col-4">{header.name}</label>
                        <FormatControls
                          format={header}
                          onChange={(updated) => updateHeaderFormat(header.id, updated)}
                          
                        />
                        <MoreOptionsDropdown>
                          <UnderlineDropdownButton
                            underline={header.underline || "none"}
                            onChange={(u) => updateHeaderFormat(header.id, { underline: u })}
                          />
                          <ColorPickerButton
                            value={header.backgroundColor || "#FFFFFF"}
                            onChange={(color) => updateHeaderFormat(header.id, { backgroundColor: color })}
                            icon={<i className="bi bi-paint-bucket"></i>}
                          />
                          <EastAsiaFontButton
                            value={header.eastAsiaFont}
                            onChange={(val) => updateHeaderFormat(header.id, { eastAsiaFont: val })}
                          />
                        </MoreOptionsDropdown>
                        {header.level > 0 && (
                          <button
                            type="button"
                            className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 ms-1 py-1"
                            onClick={() => handleDeleteHeader(header.id)}
                          >
                            <i className="bi bi-trash"/>
                          </button>
                        )}
                      </div>
                    ))}

                    {/* NEW: Agent Heading style — bold single-line paragraph, base Normal.
                        Uses the same FormatControls as everything else so font, size,
                        bold/italic, color, and the spacing/indent popover are all
                        config-driven, matching the backend's _gen_agent_heading_style. */}
                    <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1">
                      Agent Heading
                    </p>
                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">Agent Heading</label>
                      <FormatControls
                        format={agentHeadingFormat}
                        onChange={setAgentHeadingFormat}
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={agentHeadingFormat.underline || "none"}
                          onChange={(u) =>
                            setAgentHeadingFormat({ ...agentHeadingFormat, underline: u })
                          }
                        />
                        <EastAsiaFontButton
                          value={agentHeadingFormat.eastAsiaFont}
                          onChange={(val) =>
                            setAgentHeadingFormat({ ...agentHeadingFormat, eastAsiaFont: val })
                          }
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1">
                      Reference Format
                    </p>

                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4 rfm-ref-style-row">
                      <label className="col-4 d-flex align-items-center mb-0">
                        Reference style
                        <i
                          className="bi bi-eye ev-fs-12 ms-1 rfm-preview-icon-hover"
                          onClick={() => setShowBibPreview(true)}
                          title="Preview"
                          role="button"
                        ></i>
                      </label>
                      <CustomDropdown
                        value={refStyle?.fontFamily || "Arial"}
                        options={fontOptions}
                        className="w-25 flex-shrink-0"
                        onChange={(val) => setRefStyle({ ...refStyle, fontFamily: val })}
                        isFontSelector={true}
                      />

                      <FontSizeInput
                        value={refStyle?.fontSize || 10}
                        onChange={(val) => setRefStyle({ ...refStyle, fontSize: val })}
                      />

                      <button
                        type="button"
                        className={`ev-btn-small ev-message-card2 ev-card-3 me-1 ev-btn-border-square ${refStyle?.bold ? 'ev-bold-large list-element-selected' : 'ev-reg'}`}
                        onClick={() => setRefStyle({ ...refStyle, bold: !refStyle?.bold })}
                      >
                        B
                      </button>

                      <button
                        type="button"
                        className={`ev-btn-small ev-message-card2 ev-card-3 me-1 ev-btn-border-square ${refStyle?.italic ? 'ev-bold-large list-element-selected' : 'ev-reg'}`}
                        onClick={() => setRefStyle({ ...refStyle, italic: !refStyle?.italic })}
                      >
                        <span className="ev-italic-large">I</span>
                      </button>

                      <ColorPickerButton
                        value={refStyle?.color || "#000000"}
                        onChange={(color) => setRefStyle({ ...refStyle, color })}
                      />

                      <BibOptionsButton
                        bibIndent={bibIndent} onBibIndentChange={setBibIndent}
                        bibLeftMargin={bibLeftMargin} onBibLeftMarginChange={setBibLeftMargin}
                        refStyle={refStyle} onRefStyleChange={setRefStyle}
                      />

                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={refStyle?.underline || "none"}
                          onChange={(u) => setRefStyle({ ...refStyle, underline: u })}
                        />
                        <EastAsiaFontButton
                          value={refStyle?.eastAsiaFont}
                          onChange={(val) => setRefStyle({ ...refStyle, eastAsiaFont: val })}
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1 mt-2">
                      Quotation Style
                    </p>
                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">Quotation</label>
                      <FormatControls
                        format={quotationFormat}
                        onChange={setQuotationFormat}
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={quotationFormat.underline || "none"}
                          onChange={(u) => setQuotationFormat((prev) => ({ ...prev, underline: u }))}
                        />
                        <AlignmentDropdownButton
                          alignment={quotationFormat.alignment || "center"}
                          onChange={(align) => setQuotationFormat((prev) => ({ ...prev, alignment: align }))}
                        />
                        <ColorPickerButton
                          value={quotationFormat.borderColor || "#00B0F0"}
                          onChange={(color) => setQuotationFormat((prev) => ({ ...prev, borderColor: color }))}
                          icon={<i className="bi bi-quote ev-fs-12"></i>}
                          title="Border Color"
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <div className="mt-4 mb-3">
                      <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1">Page Size</p>
                      <div style={{ maxWidth: "260px" }}>
                        <PageSizeDropdown value={pageSize} onChange={setPageSize} />
                      </div>
                    </div>

                    {/* ── Margins ── */}
                    <div className="mt-4 mb-3">
                      <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1">Margins</p>
                      <div className="d-flex gap-3 align-items-stretch">
                        {/* Margins sit in a col-6 (~500px): inputs flex, preview fixed ~210px so both fit side by side */}
                        <div style={{ flex: "1 1 auto", minWidth: 0, maxWidth: 340 }}>
                          <MarginsDropdown value={marginPreset} onChange={handleMarginPresetChange} />

                          <div className={`mt-2 ${marginPreset !== "custom" ? "opacity-50" : ""}`}>
                            <div className="row g-2 mb-2">
                              <div className="col-6">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="ev-fs-11 ev-reg-p-dark me-1 text-nowrap">Top</span>
                                  <div style={{ width: 80, flexShrink: 0 }}>
                                    <NumberStepperInput value={resolvedMargins.top} step={0.1} unit='"' min={0} max={11}
                                      onChange={(val) => { switchMarginToCustom(); setMarginTop(val); }} />
                                  </div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="ev-fs-11 ev-reg-p-dark me-1 text-nowrap">Bottom</span>
                                  <div style={{ width: 80, flexShrink: 0 }}>
                                    <NumberStepperInput value={resolvedMargins.bottom} step={0.1} unit='"' min={0} max={11}
                                      onChange={(val) => { switchMarginToCustom(); setMarginBottom(val); }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row g-2 mb-2">
                              <div className="col-6">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="ev-fs-11 ev-reg-p-dark me-1 text-nowrap">{resolvedMargins.mirrored ? "Inside" : "Left"}</span>
                                  <div style={{ width: 80, flexShrink: 0 }}>
                                    <NumberStepperInput value={resolvedMargins.left} step={0.1} unit='"' min={0} max={11}
                                      onChange={(val) => { switchMarginToCustom(); setMarginLeft(val); }} />
                                  </div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="ev-fs-11 ev-reg-p-dark me-1 text-nowrap">{resolvedMargins.mirrored ? "Outside" : "Right"}</span>
                                  <div style={{ width: 80, flexShrink: 0 }}>
                                    <NumberStepperInput value={resolvedMargins.right} step={0.1} unit='"' min={0} max={11}
                                      onChange={(val) => { switchMarginToCustom(); setMarginRight(val); }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="row g-2">
                              <div className="col-6">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="ev-fs-11 ev-reg-p-dark me-1 text-nowrap">Gutter</span>
                                  <div style={{ width: 80, flexShrink: 0 }}>
                                    <NumberStepperInput value={resolvedMargins.gutter} step={0.1} unit='"' min={0} max={5}
                                      onChange={(val) => { switchMarginToCustom(); setMarginGutter(val); }} />
                                  </div>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="ev-fs-11 ev-reg-p-dark me-1">Gutter pos.</span>
                                  <div style={{ width: 80, flexShrink: 0 }}>
                                    <CustomDropdown value={resolvedMargins.gutterPosition === "top" ? "Top" : "Left"}
                                      options={GUTTER_POSITION_OPTIONS} className="w-100"
                                      onChange={(val) => { switchMarginToCustom(); setMarginGutterPosition(val.toLowerCase()); }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          className="d-flex flex-column justify-content-center px-2 py-1"
                          style={{ border: "1px solid var(--color10)", borderRadius: 8, flexShrink: 0 }}
                        >
                          <div className="d-flex gap-2">
                            {[
                              { label: "Portrait", title: "Regular pages", margins: resolvedMargins, landscape: false },
                              { label: "Landscape", title: "Pages with wide tables", margins: toLandscapeMargins(resolvedMargins), landscape: true },
                            ].map(({ label, title, margins, landscape }) => (
                              <div key={label} title={title} className="d-flex flex-column align-items-center" style={{ width: 96 }}>
                                {/* Same square frame for both so the two columns line up */}
                                <div className="d-flex align-items-center justify-content-center" style={{ width: 90, height: 90 }}>
                                  <MarginPreview {...margins} landscape={landscape} size={86} />
                                </div>
                                <span className="ev-fs-10 fw-semibold ev-med-p-blue mt-1 mb-1 text-nowrap">{label}</span>
                                <MarginValues {...margins} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex mt-4">
                      <div className="w-50 pe-3">
                        <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1">
                          Numbering Format
                        </p>
                        <p className="ev-fs-11 ev-med-p-blue mb-2">(also applicable for table of content)</p>

                        <div className="d-flex gap-3 mb-3">
                          <div style={{ cursor: "pointer", flex: 1 }} onClick={() => setNumberingFormat("none")}>
                            <div className="mb-1 vh-3">
                              <div className="d-flex justify-content-end align-items-center mb-1 vh-3">
                                <span className="ev-fs-11 ev-reg-p-dark me-2">Start Value</span>
                                <CustomDropdown
                                  value={numberingStartValue}
                                  options={[0,1,2,3,4,5,6,7,8,9,10]}
                                  className="w-40"
                                  onChange={(val) => { setNumberingStartValue(Number(val)); setNumberingFormat("none"); }}
                                />
                              </div>
                            </div>
                            <div className="ev-padding-small ev-radius-small"
                              style={{ 
                                   backgroundColor: "var(--background2)", 
                                   color: "var(--color5)", 
                                border: numberingFormat === "none" ? 'var(--px-2) solid var(--color2)' : 'var(--px-1) solid var(--color17)',
                                transition: "all 0.2s ease"
                              }}>
                              <div className="d-flex align-items-center mb-2 border-bottom pb-1" style={{ borderColor: "var(--color17)" }}>
                                <span className="fw-bold">Numeric</span>
                              </div>
                              <div className="ev-fs-13 lh-base">
                                <div>{numberingStartValue}{headingNumberPeriod ? "." : ""}</div>
                                <div className="ps-1">{numberingStartValue}.1</div>
                                <div className="ps-1">{numberingStartValue}.2</div>
                                <div className="">{numberingStartValue + 1}{headingNumberPeriod ? "." : ""}</div>
                                <div className="ps-1">{numberingStartValue + 1}.1</div>
                                <div className="ps-2">{numberingStartValue + 1}.1.1</div>
                              </div>
                            </div>
                          </div>

                          <div style={{ flex: 1 }}>
                            <div className="d-flex justify-content-end align-items-center mb-1 vh-3">
                               <span 
                                  className="ev-fs-11 ev-reg-p-dark me-2 text-nowrap" 
                                  style={{ opacity: numberingFormat === "none" ? 0.5 : 1 }}
                                >
                                Start Value
                              </span>
                              <CustomDropdown
                                value={numberingStartChar}
                                options={['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V','W','X','Y','Z']}
                                className="w-40"
                                  onChange={(val) => {
                                    setNumberingStartChar(val); 
                                    setNumberingFormat(val);
                                }}
                              />
                            </div>
                            <div className="ev-padding-small ev-radius-small"
                              style={{ 
                                backgroundColor: "var(--background2)", 
                                color: "var(--color5)", 
                                border: numberingFormat === numberingStartChar ? 'var(--px-2) solid var(--color2)' : 'var(--px-1) solid var(--color17)',
                                transition: "all 0.2s ease",
                                cursor: "pointer"
                              }}
                              onClick={() => setNumberingFormat(numberingStartChar)}
                            >
                              <div className="d-flex align-items-center mb-2 border-bottom pb-1" style={{ borderColor: "var(--color17)" }}>
                                <span className="fw-bold">Alphabet</span>
                              </div>
                              <div className="ev-fs-13 lh-base">
                                <div>{numberingStartChar}{headingNumberPeriod ? "." : ""}</div>
                                <div className="ps-1">{numberingStartChar}.1</div>
                                <div className="ps-2">{numberingStartChar}.1.1</div>
                                <div className="ps-3">{numberingStartChar}.1.1.1</div>
                                <div className="ps-1">{numberingStartChar}.2</div>
                                <div className="ps-2">{numberingStartChar}.2.1</div>
                              </div>
                            </div>
                          </div>
                        </div>


                        
                        
                        
                      </div>

                      <div className="w-50 ps-3">
                      <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-2">
                        Citation Format
                      </p>
                        {(() => {
                          // let citationPreviewText = "1";
                          const citationPreviewText = CITATION_STYLE_OPTIONS.find((o) => o.value === citationFormat)?.previewText ?? "1";

                          return (
                            <>
                              <div className="mb-3">
                                <label className="ev-fs-13 ev-reg-p-dark d-block mb-1">Inline</label>
                                <div className="btn-group custom-btn-group-outer overflow-hidden w-100" role="group">
                                  <button
                                    type="button"
                                    className={`btn custom-btn-group-inner ev-btn-small ${citationInlineStyle === 'subscript' ? 'ev-btn-blue-fill-round' : 'ev-btn-text-only-fill'}`}
                                    onClick={() => setCitationInlineStyle("subscript")}
                                    title="Subscript"
                                  >
                                    <span className="ev-fs-12 text-nowrap">x<sub className="opacity-75"> {citationPreviewText}</sub></span>
                                  </button>
                          
                                  <button
                                    type="button"
                                    className={`btn custom-btn-group-inner ev-btn-small ${citationInlineStyle === 'superscript' ? 'ev-btn-blue-fill-round' : 'ev-btn-text-only-fill'}`}
                                    onClick={() => setCitationInlineStyle("superscript")}
                                    title="Superscript"
                                  >
                                    <span className="ev-fs-12 text-nowrap">x<sup className="opacity-75">{citationPreviewText}</sup></span>
                                  </button>

                                  <button
                                    type="button"
                                    className={`btn custom-btn-group-inner ev-btn-small ${citationInlineStyle === 'numeric' ? 'ev-btn-blue-fill-round' : 'ev-btn-text-only-fill'}`}
                                    onClick={() => setCitationInlineStyle("numeric")}
                                    title="Standard"
                                  >
                            <span  className="ev-fs-10 text-nowrap"> [{citationPreviewText}]</span>
                                  </button>
                                </div>
                                <div className="d-flex align-items-center ms-1 mt-2">
                                  <FormatControls
                                    format={inlineCitationFormat}
                                    onChange={setInlineCitationFormat}
                                    fontFamilyWidth="w-30"
                                    hideLineSpacing
                                    hideSpacing
                                  />
                                  <MoreOptionsDropdown>
                                    <UnderlineDropdownButton
                                      underline={inlineCitationFormat.underline || "none"}
                              onChange={(u) =>
                                setInlineCitationFormat({ ...inlineCitationFormat, underline: u })
                              }
                                    />
                                    <EastAsiaFontButton
                                      value={inlineCitationFormat.eastAsiaFont}
                              onChange={(val) =>
                                setInlineCitationFormat({ ...inlineCitationFormat, eastAsiaFont: val })
                              }
                                    />
                                  </MoreOptionsDropdown>
                                </div>
                              </div>
                            </>
                          );
                        })()}

                        <div className="mb-2">
                          <label className="ev-fs-13 ev-reg-p-dark d-block mb-1">Bibliography</label>
                          <LabeledDropdown
                            value={citationFormat}
                            options={CITATION_STYLE_OPTIONS}
                            onChange={setCitationFormat}
                            className="w-100"
                          />
                        </div>

                        {/* <p className="ev-bold-reg ev-med-p-blue ev-fs-14 mb-2 mt-3">
                          Spacing Options
                        </p>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <label className="ev-fs-13 ev-reg-p-dark">Line Spacing</label>
                          
                            <CustomDropdown
                              value={lineSpacing}
                              options={lineSpacingOptions}
                             className="w-20"
                              onChange={(val) => setLineSpacing(Number(val))}
                            />
                          
                      </div>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <label className="ev-fs-13 ev-reg-p-dark">Paragraph Spacing</label>
                            <CustomDropdown
                              value={paragraphSpacing}
                              options={paragraphSpacingOptions}
                              className="w-20"
                              onChange={(val) => setParagraphSpacing(Number(val))}
                            />
                        </div> */}

                    </div>
                    </div>
                    {/* <p className="ev-bold-reg ev-med-p-blue ev-fs-14 mb-2">
                          Front Page
                        </p>
                        <div className="ev-card-2 p-2">
                          {frontPagesLoading ? (
                            <span className="ev-small-p-blue">Loading...</span>
                          ) : frontPages.length === 0 ? (
                            <span className="ev-small-p-blue">No front pages available</span>
                          ) : (
                            <>
                              <div className="d-flex flex-wrap gap-2">
                                <div
                                  className="ev-radius-small p-2 cursor-pointer ev-card-3 text-center"
                                  style={{
                                    width: "80px",
                                    border: !selectedFrontPage
                                      ? "var(--px-2) solid var(--color2)"
                                      : "var(--px-1) solid var(--color17)",
                                  }}
                                  onClick={() => setSelectedFrontPage(null)}
                                >
                                  <i className="bi bi-x-circle ev-fs-15 d-block mb-1"></i>
                                  <span className="ev-fs-10 ev-reg-p-dark">None</span>
                  </div>

                                {frontPages.map((fp) => (
                                  <div
                                    key={fp.s3_key}
                                    className="ev-radius-small p-2 cursor-pointer ev-card-3 text-center"
                                    style={{
                                      width: "80px",
                                      border: selectedFrontPage?.s3_key === fp.s3_key
                                        ? "var(--px-2) solid var(--color2)"
                                        : "var(--px-1) solid var(--color17)",
                                    }}
                                    onClick={() => setSelectedFrontPage(fp)}
                                  >
                                    <i className="bi bi-file-earmark-text ev-fs-15 d-block mb-1"></i>
                                    <span className="ev-fs-10 ev-reg-p-dark text-truncate d-block">
                                      {fp.display_name}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {selectedFrontPage && (
                                <div className="mt-2 ev-message-card1 ev-padding-xsmall ev-radius-small d-flex align-items-center justify-content-between">
                                  <div className="d-flex align-items-center">
                                    <i className="bi bi-check-circle-fill text-success me-2"></i>
                                    <span className="ev-fs-11 ev-reg-p-dark">{selectedFrontPage.display_name}</span>
                                  </div>
                                  <button
                                    type="button"
                                    className="ev-btn-blue-fill-round ev-btn-xsmall"
                                    onClick={() => setPreviewFrontPagePath(selectedFrontPage.s3_key)}
                                  >
                                    <i className="bi bi-eye me-1"></i>Preview
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>  */}
                    </div>
                 

                  {/* --- RIGHT SIDE --- */}
                  <div className="col-6">
                    <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-1">
                      General Tables
                    </p>
                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">Table Text</label>
                      <FormatControls
                        format={generalTableTextFormat}
                        onChange={setGeneralTableTextFormat}
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={generalTableTextFormat.underline || "none"}
                          onChange={(u) =>
                            setGeneralTableTextFormat({ ...generalTableTextFormat, underline: u })
                          }
                        />
                        <BulletIndentButton
                          value={tableBulletIndentPt}
                          onChange={setTableBulletIndentPt}
                        />
                        <CellAlignmentButton
                          vertical={tableCellVerticalAlign}
                          horizontal={tableCellHorizontalAlign}
                          onChangeVertical={setTableCellVerticalAlign}
                          onChangeHorizontal={setTableCellHorizontalAlign}
                        />
                        <EastAsiaFontButton
                          value={generalTableTextFormat.eastAsiaFont}
                          onChange={(val) =>
                            setGeneralTableTextFormat({ ...generalTableTextFormat, eastAsiaFont: val })
                          }
                        />
                      </MoreOptionsDropdown>
                    </div>
                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">Table Header</label>
                      <FormatControls
                        format={generalTableHeaderFormat}
                        onChange={setGeneralTableHeaderFormat}
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={generalTableHeaderFormat.underline || "none"}
                          onChange={(u) =>
                            setGeneralTableHeaderFormat({ ...generalTableHeaderFormat, underline: u })
                          }
                        />
                        <CellAlignmentButton
                          vertical={tableHeaderVerticalAlign}
                          horizontal={tableHeaderHorizontalAlign}
                          onChangeVertical={setTableHeaderVerticalAlign}
                          onChangeHorizontal={setTableHeaderHorizontalAlign}
                        />
                        <EastAsiaFontButton
                          value={generalTableHeaderFormat.eastAsiaFont}
                          onChange={(val) =>
                            setGeneralTableHeaderFormat({ ...generalTableHeaderFormat, eastAsiaFont: val })
                          }
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">Table Footer</label>
                      <FormatControls
                        format={generalTableFooterFormat}
                        onChange={setGeneralTableFooterFormat}
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={generalTableFooterFormat.underline || "none"}
                          onChange={(u) =>
                            setGeneralTableFooterFormat({ ...generalTableFooterFormat, underline: u })
                          }
                        />
                        <EastAsiaFontButton
                          value={generalTableFooterFormat.eastAsiaFont}
                          onChange={(val) =>
                            setGeneralTableFooterFormat({ ...generalTableFooterFormat, eastAsiaFont: val })
                          }
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <p className="mb-1 ev-fs-13 ev-reg-p-dark">Table Design</p>
                    <div className="ev-card-2">
                      <div className="d-flex justify-content-between align-items-start flex-wrap">
                        <div className="d-flex gap-2 flex-wrap">
                          {TABLE_DESIGNS.map((design) => (
                            <TableDesignThumbnail
                              key={design.id}
                              design={design}
                              isSelected={generalTableDesign === design.id}
                              onClick={setGeneralTableDesign}
                              headerFill={generalTableHeaderFormat.backgroundColor}
                              borderColor={generalTableBorderColor}
                              onPreview={(d) =>
                                setPreviewDesign({
                                  design: d,
                                  headerFormat: generalTableHeaderFormat,
                                  bodyFormat: generalTableTextFormat,
                                  borderColor: generalTableBorderColor,
                                })
                              }
                            />
                          ))}
                        </div>
                        {/* One picker for both colours. The Header/Border toggle
                            lives inside the popover via topSlot, so the row beside
                            the thumbnails stays as uncluttered as it was. */}
                        <ColorPickerButton
                          value={
                            tableColorMode === "header"
                              ? (generalTableHeaderFormat.backgroundColor || "#FFFFFF")
                              : (generalTableBorderColor || "#000000")
                          }
                          onChange={(color) =>
                            tableColorMode === "header"
                              ? setGeneralTableHeaderFormat({ ...generalTableHeaderFormat, backgroundColor: color })
                              : setGeneralTableBorderColor(color)
                          }
                          icon={<i className="bi bi-brush ev-fs-14 ms-1"></i>}
                          iconOnly
                          title="Table colours"
                          topSlot={(
                            <div className="ev-view-toggle-shell ev-view-toggle-shell--compact">
                              <button
                                type="button"
                                className={`ev-view-toggle-btn ${tableColorMode === "header" ? "active" : ""}`}
                                onClick={() => setTableColorMode("header")}
                              >
                                Header color
                              </button>
                              <button
                                type="button"
                                className={`ev-view-toggle-btn ${tableColorMode === "border" ? "active" : ""}`}
                                onClick={() => setTableColorMode("border")}
                              >
                                Border color
                              </button>
                            </div>
                          )}
                        />
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-between me-4 mt-2 mb-2">
                      <label className="ev-fs-12 ev-reg-p-dark">Use same table design for List of Abbreviations</label>
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={abbreviationUseTableDesign}
                          onChange={(e) => setAbbreviationUseTableDesign(e.target.checked)}
                        />
                      </div>
                    </div>

                    <p className="ev-bold-reg  ev-fs-14 ev-med-p-blue mb-2 mt-2">
                      Tables of Contents / Tables / Figures
                    </p>

                    {/* TOC Custom row — Add button + TOC Level selector */}
                    <div className="d-flex mb-2 ev-fs-13 align-items-center ev-reg-p-dark me-4">
                      <label className="col-4">TOC (Custom)</label>
                      <button
                        type="button"
                        className="ev-btn-blue-fill-round ev-btn-small d-flex align-items-center gap-2"
                        onClick={handleAddTocStyle}
                      >
                        <i className="bi bi-plus-circle-fill ev-fs-10"></i>
                        Add TOC Style
                      </button>
                      <TocOptionsButton
                        tocLevelNum={tocLevelNum}
                        onTocLevelChange={setTocLevelNum}
                        tocNumberSpacing={tocNumberSpacing}
                        onTocNumberSpacingChange={setTocNumberSpacing}
                        tocShowDots={tocShowDots}
                        onTocShowDotsChange={setTocShowDots}
                        headingNumberPeriod={headingNumberPeriod}
                        onHeadingNumberPeriodChange={setHeadingNumberPeriod}
                        includeLists={includeLists}
                        onIncludeListsChange={setIncludeLists}
                        maxHeaderLevel={maxHeaderLevel}
                      />
                    </div>

                    {/* Per-level TOC style rows — same pattern as Paragraphs */}
                    {tocStyleFormats.map((tocStyle) => (
                      <div key={tocStyle.id} className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                        <label className="col-4">{tocStyle.name}</label>
                        <FormatControls
                          format={tocStyle}
                          onChange={(updated) => updateTocStyleFormat(tocStyle.id, updated)}
                          fontFamilyWidth="w-18"
                        />
                        <MoreOptionsDropdown>
                          <UnderlineDropdownButton
                            underline={tocStyle.underline || "none"}
                            onChange={(u) => updateTocStyleFormat(tocStyle.id, { underline: u })}
                          />
                          <EastAsiaFontButton
                            value={tocStyle.eastAsiaFont}
                            onChange={(val) => updateTocStyleFormat(tocStyle.id, { eastAsiaFont: val })}
                          />
                        </MoreOptionsDropdown>
                        {tocStyle.level > 0 && (
                          <button
                            type="button"
                            className="ev-btn-red-fill-round ev-radius-xsmall pe-2 ps-2 ms-1 py-1"
                            onClick={() => handleDeleteTocStyle(tocStyle.id)}
                          >
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </div>
                    ))}

                    <p className="ev-bold-reg  ev-fs-14 ev-med-p-blue mb-2 mt-2">
                      List of Tables
                    </p>
                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">List of Tables</label>
                      <FormatControls
                        format={lotStyleFormat}
                        onChange={(updated) => setLotStyleFormat({ ...lotStyleFormat, ...updated })}
                        fontFamilyWidth="w-18"
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={lotStyleFormat.underline || "none"}
                          onChange={(u) => setLotStyleFormat({ ...lotStyleFormat, underline: u })}
                        />
                        <EastAsiaFontButton
                          value={lotStyleFormat.eastAsiaFont}
                          onChange={(val) => setLotStyleFormat({ ...lotStyleFormat, eastAsiaFont: val })}
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <p className="ev-bold-reg  ev-fs-14 ev-med-p-blue mb-2 mt-2">
                      List of Figures
                    </p>
                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">List of Figures</label>
                      <FormatControls
                        format={lofStyleFormat}
                        onChange={(updated) => setLofStyleFormat({ ...lofStyleFormat, ...updated })}
                        fontFamilyWidth="w-18"
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={lofStyleFormat.underline || "none"}
                          onChange={(u) => setLofStyleFormat({ ...lofStyleFormat, underline: u })}
                        />
                        <EastAsiaFontButton
                          value={lofStyleFormat.eastAsiaFont}
                          onChange={(val) => setLofStyleFormat({ ...lofStyleFormat, eastAsiaFont: val })}
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <p className="mb-1 ev-reg-p-dark ev-fs-13">Table Design</p>
                    <div className="ev-card-2 p-2">
                      <div className="d-flex gap-2 flex-wrap">
                        
                        {TOC_DESIGNS.map((design) => (
                          <div key={design.id} className="d-flex flex-column align-items-center me-2">
                            <button
                              type="button"
                              className="ev-btn-blue-fill-round ev-btn-xsmall mb-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (design.type === 'list') setShowTocPreview(true);
                              }}
                            >
                              Preview
                            </button>

                            <div
                              className="ev-radius-small p-1 ev-card-3 position-relative cursor-pointer"
                              style={{
                                width: "60px", 
                                height: "40px", 
                                border: tocTableDesign === design.id ? "var(--px-2) solid var(--color2)" : "var(--px-1) solid var(--color17)",
                                overflow: "hidden"
                              }}
                              onClick={() => setTocTableDesign(design.id)}
                            >
                              {design.type === 'list' && (
                                <div className="d-flex flex-column justify-content-center h-100 px-2 gap-1">
                                  <div className="w-100 bg-secondary opacity-25" style={{height: "4px"}}></div>
                                  <div className="w-75 bg-secondary opacity-25" style={{height: "4px"}}></div>
                                  <div className="w-100 bg-secondary opacity-25" style={{height: "4px"}}></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}

                      </div>
                    </div>
                    <p className="ev-bold-reg ev-fs-14 ev-med-p-blue mb-2 mt-2">
                      Caption Placement
                    </p>

                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">Caption (Table/Figure)</label>
                      <FormatControls
                        format={captionFormat}
                        onChange={setCaptionFormat}
                      />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={captionFormat.underline || "none"}
                          onChange={(u) =>
                            setCaptionFormat({ ...captionFormat, underline: u })
                          }
                        />
                        <EastAsiaFontButton
                          value={captionFormat.eastAsiaFont}
                          onChange={(val) =>
                            setCaptionFormat({ ...captionFormat, eastAsiaFont: val })
                          }
                        />
                      </MoreOptionsDropdown>
                    </div>

                    <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2 me-4">
                      <label className="col-4">Image Footnotes</label>
                      <FormatControls format={imageFootNotesFormat} onChange={setImageFootNotesFormat} />
                      <MoreOptionsDropdown>
                        <UnderlineDropdownButton
                          underline={imageFootNotesFormat.underline || "none"}
                          onChange={(u) =>
                            setImageFootNotesFormat({ ...imageFootNotesFormat, underline: u })
                          }
                        />
                        <EastAsiaFontButton
                          value={imageFootNotesFormat.eastAsiaFont}
                          onChange={(val) =>
                            setImageFootNotesFormat({ ...imageFootNotesFormat, eastAsiaFont: val })
                          }
                        />
                      </MoreOptionsDropdown>
                    </div>

                    

                    <div className="ev-card-2 p-3 py-0">
                      <div className="row">
                        {/* FIGURE COLUMN */}
                        <div className="col-6">
                          <p className="ev-bold-reg ev-med-p-blue ev-fs-12 mb-2 mt-2">Figure Caption Placement</p>
                          <div className="btn-group custom-btn-group-outer overflow-hidden w-100 mb-2 p-0" role="group">
                            <button
                              type="button"
                              className={`btn custom-btn-group-inner ev-fs-12 ev-btn-small ${figureCaptionPlacement === 'above' ? 'ev-btn-blue-fill-round' : 'ev-btn-text-only-fill'}`}
                              onClick={() => setFigureCaptionPlacement("above")}
                            >
                              Above
                            </button>
                            <button
                              type="button"
                              className={`btn custom-btn-group-inner ev-fs-12 ev-btn-small ${figureCaptionPlacement === 'below' ? 'ev-btn-blue-fill-round' : 'ev-btn-text-only-fill'}`}
                              onClick={() => setFigureCaptionPlacement("below")}
                            >
                              Below
                            </button>
                          </div>

                          {/* Use heading number */}
                          <div className="d-flex align-items-center justify-content-between">
                            <label className="ev-fs-12 ev-reg-p-dark">Use heading number</label>
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={figureUseHeadingNum}
                                onChange={(e) => setFigureUseHeadingNum(e.target.checked)}
                              />
                            </div>
                          </div>

                          {/* Heading Level */}
                          {/* <div className={`row align-items-center mb-2 ${!figureUseHeadingNum ? 'opacity-50' : ''}`}>
                            <label className="col-5 ev-fs-12 ev-reg-p-dark">Heading Level</label>
                            <div className="col-7 d-flex justify-content-end ">
                              <CustomDropdown
                                value={figureHeadingLevel}
                                options={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
                                className="w-30"
                                onChange={(val) => setFigureHeadingLevel(Number(val))}
                                disabled={!figureUseHeadingNum}
                              />
                            </div>
                          </div> */}

                          {/* Connector */}
                          <div className={`row align-items-center mb-2 ${!figureUseHeadingNum ? 'opacity-50' : ''}`}>
                            <label className="col-5 ev-fs-12 ev-reg-p-dark">Connector</label>
                            <div className="col-7 d-flex justify-content-end ">
                              <input
                                type="text"
                                maxLength={5}
                                disabled={!figureUseHeadingNum}
                                value={figureConnector}
                                placeholder="-"
                                onChange={(e) => setFigureConnector(e.target.value)}
                                className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 text-center  w-30"
                              />
                            </div>
                          </div>
                          {/* Non-breaking connector: the "-" never splits the number at a line end */}
                          <div className={`d-flex align-items-center justify-content-between mb-2 ${!figureUseHeadingNum ? 'opacity-50' : ''}`}>
                            <label className="ev-fs-12 ev-reg-p-dark">Non-breaking hyphen</label>
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                disabled={!figureUseHeadingNum}
                                checked={figureConnectorNb}
                                onChange={(e) => setFigureConnectorNb(e.target.checked)}
                              />
                            </div>
                          </div>
                          {/* Suffix */}
                          <div className="row align-items-center mb-2">
                            <label className="col-5 ev-fs-12 ev-reg-p-dark">Suffix</label>
                            <div className="col-7 d-flex justify-content-end">
                              <input
                                type="text"
                                maxLength={5}
                                value={figureSuffix}
                                placeholder="."
                                onChange={(e) => setFigureSuffix(e.target.value)}
                                className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 text-center w-30"
                              />
                            </div>
                          </div>
                          {/* Live preview */}
                          {figureUseHeadingNum && (
                            <p className="ev-fs-11 ev-reg-p-dark mt-1">
                              Preview: <span className="ev-bold-reg">
                                Figure {Array.from({ length: figureHeadingLevel }, (_, i) => i+1 ).join(".")}{figureConnectorNb ? figureConnector.replace(/-/g, '‑') : figureConnector}1{figureSuffix} Title
                              </span>
                            </p>
                          )}
                        </div>

                        {/* TABLE COLUMN */}
                        <div className="col-6">
                          <p className="ev-bold-reg ev-med-p-blue ev-fs-12 mb-2 mt-2">Table Caption Placement</p>
                          <div className="btn-group custom-btn-group-outer overflow-hidden w-100 mb-2 p-0" role="group">
                            <button
                              type="button"
                              className={`btn custom-btn-group-inner ev-fs-12 ev-btn-small ${tableCaptionPlacement === "above" ? "ev-btn-blue-fill-round" : "ev-btn-text-only-fill"}`}
                              onClick={() => setTableCaptionPlacement("above")}
                            >
                              Above
                            </button>
                            <button
                              type="button"
                              className={`btn custom-btn-group-inner ev-fs-12 ev-btn-small ${tableCaptionPlacement === "below" ? "ev-btn-blue-fill-round" : "ev-btn-text-only-fill"}`}
                              onClick={() => setTableCaptionPlacement("below")}
                            >
                              Below
                            </button>
                          </div>

                          {/* Use heading number */}
                          <div className="d-flex align-items-center justify-content-between">
                            <label className="ev-fs-12 ev-reg-p-dark">Use heading number</label>
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                checked={tableUseHeadingNum}
                                onChange={(e) => setTableUseHeadingNum(e.target.checked)}
                              />
                            </div>
                          </div>

                          {/* Heading Level */}
                          {/* <div className={`row align-items-center mb-2 ${!tableUseHeadingNum ? 'opacity-50' : ''}`}>
                            <label className="col-5 ev-fs-12 ev-reg-p-dark">Heading Level</label>
                            <div className="col-7 d-flex justify-content-end ">
                              <CustomDropdown
                                value={tableHeadingLevel}
                                options={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
                                className="w-30"
                                onChange={(val) => setTableHeadingLevel(Number(val))}
                                disabled={!tableUseHeadingNum}
                              />
                            </div>
                          </div> */}

                          {/* Connector */}
                          <div className={`row align-items-center mb-2 ${!tableUseHeadingNum ? 'opacity-50' : ''}`}>
                            <label className="col-5 ev-fs-12 ev-reg-p-dark">Connector</label>
                            <div className="col-7 d-flex justify-content-end ">
                              <input
                                type="text"
                                maxLength={5}
                                disabled={!tableUseHeadingNum}
                                value={tableConnector}
                                placeholder="-"
                                onChange={(e) => setTableConnector(e.target.value)}
                                className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 text-center  w-30"
                              />
                            </div>
                          </div>
                          {/* Non-breaking connector: the "-" never splits the number at a line end */}
                          <div className={`d-flex align-items-center justify-content-between mb-2 ${!tableUseHeadingNum ? 'opacity-50' : ''}`}>
                            <label className="ev-fs-12 ev-reg-p-dark">Non-breaking hyphen</label>
                            <div className="form-check form-switch mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                disabled={!tableUseHeadingNum}
                                checked={tableConnectorNb}
                                onChange={(e) => setTableConnectorNb(e.target.checked)}
                              />
                            </div>
                          </div>
                          {/* Suffix */}
                          <div className="row align-items-center mb-2">
                            <label className="col-5 ev-fs-12 ev-reg-p-dark">Suffix</label>
                            <div className="col-7 d-flex justify-content-end">
                              <input
                                type="text"
                                maxLength={5}
                                value={tableSuffix}
                                placeholder="."
                                onChange={(e) => setTableSuffix(e.target.value)}
                                className="formField3 ev-btn-border-square ev-btn-small ev-message-card2 ev-card-3 text-center w-30"
                              />
                            </div>
                          </div>
                          {/* Live preview */}
                          {tableUseHeadingNum && (
                            <p className="ev-fs-11 ev-reg-p-dark mt-1">
                              Preview: <span className="ev-bold-reg">
                                Table {Array.from({ length: tableHeadingLevel }, (_, i) => i+1 ).join(".")}{tableConnectorNb ? tableConnector.replace(/-/g, '‑') : tableConnector}1{tableSuffix} Title
                              </span>
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Cross-Ref Styles — separate row so both stay aligned */}
                      <div className="row">
                        <div className="col-6">
                          <p className="ev-bold-reg ev-med-p-blue ev-fs-12 mb-2 mt-3">Figure Cross-Ref</p>
                          <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2">
                            <FormatControls
                              format={figureRefFormat}
                              onChange={setFigureRefFormat}
                              fontFamilyWidth="w-30"
                              hideLineSpacing
                              hideSpacing
                            >
                              <MoreOptionsDropdown>
                                <UnderlineDropdownButton
                                  underline={figureRefFormat.underline || "none"}
                                  onChange={(u) =>
                                    setFigureRefFormat({ ...figureRefFormat, underline: u })
                                  }
                                />
                                <EastAsiaFontButton
                                  value={figureRefFormat.eastAsiaFont}
                                  onChange={(val) =>
                                    setFigureRefFormat({ ...figureRefFormat, eastAsiaFont: val })
                                  }
                                />
                              </MoreOptionsDropdown>
                            </FormatControls>
                          </div>
                        </div>
                        <div className="col-6">
                          <p className="ev-bold-reg ev-med-p-blue ev-fs-12 mb-2 mt-3">Table Cross-Ref</p>
                          <div className="d-flex align-items-center ev-fs-13 ev-reg-p-dark mb-2">
                            <FormatControls
                              format={tableRefFormat}
                              onChange={setTableRefFormat}
                              fontFamilyWidth="w-30"
                              hideLineSpacing
                              hideSpacing
                            >
                              <MoreOptionsDropdown>
                                <UnderlineDropdownButton
                                  underline={tableRefFormat.underline || "none"}
                                  onChange={(u) =>
                                    setTableRefFormat({ ...tableRefFormat, underline: u })
                                  }
                                />
                                <EastAsiaFontButton
                                  value={tableRefFormat.eastAsiaFont}
                                  onChange={(val) =>
                                    setTableRefFormat({ ...tableRefFormat, eastAsiaFont: val })
                                  }
                                />
                              </MoreOptionsDropdown>
                            </FormatControls>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                  <div className="col-12">
                    <ListsSection
                      lists={lists}
                      setLists={setLists}
                      paragraphFormats={paragraphFormats}
                      updateParagraphFormat={updateParagraphFormat}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 text-end flex-shrink-0">
              {!canSave && (
                <span className="ev-small-p-blue me-2">
                  <i className="bi bi-eye me-1"></i> View only
                </span>
              )}
              <button
                className="ev-btn-blue-outline-round me-2"
                onClick={onClose}
                disabled={saving}
              >
                {canSave ? 'Cancel' : 'Close'}
              </button>
              {canSave && (
                <button
                  className="ev-btn-blue-fill-round"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Configuration"}
                </button>
              )}
            </div>

          </div>
        </div>
      </div>
      
      {previewDesign && (
        <TablePreviewModal
          design={previewDesign.design}
          headerFormat={previewDesign.headerFormat}
          bodyFormat={previewDesign.bodyFormat}
          borderColor={previewDesign.borderColor}
          tableBulletIndentPt={tableBulletIndentPt}
          onClose={() => setPreviewDesign(null)}
        />
      )}
      {showTocPreview && (
        <TocPreviewModal
          onClose={() => setShowTocPreview(false)}
          format={tocTableTextFormat}
          tocLevelNum={tocLevelNum}
          tocNumberSpacing={tocNumberSpacing}
          headingNumberPeriod={headingNumberPeriod}
          tocShowDots={tocShowDots}
          headerFormats={headerFormats}
          tocStyleFormats={tocStyleFormats}
        />
      )}

      {showBibPreview && (
        <BibPreviewModal
          onClose={() => setShowBibPreview(false)}
          bibIndent={bibIndent}
          bibLeftMargin={bibLeftMargin}
          refStyle={refStyle}
        />
      )}

      {previewFrontPagePath && (
        <DocumentPreview
          isOpen={!!previewFrontPagePath}
          onClose={() => setPreviewFrontPagePath(null)}
          s3_path={previewFrontPagePath}
          bucket_type="output"
          documentName={previewFrontPagePath.split("/").pop()}
        />
      )}
    </>,
    document.body
  );
};

export default ReportFormattingModal;